import * as AppRoutesUrl from 'src/utils/constants/app-routes'
import { TimeoutUtil } from 'src/utils/helper/timer'
import * as Common from 'src/utils/constants/common'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import 'dayjs/locale/en'
import 'dayjs/locale/de'
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)
import { isEqual, flattenDeep, find, findIndex, uniq, uniqWith, differenceBy, isEmpty } from 'lodash'
import { Toaster } from 'src/utils/helper/toaster'
import i18n from 'src/assets/i18n/i18n'
import XRegExp from 'xregexp'
import SparkMD5 from 'spark-md5'
import Api from 'fou-api'
import { isNonActiveStatus } from 'src/utils/helper/contractTermination'
import { CONSOLIDATED } from 'src/utils/constants/contract-termination'
import { TERMINATED_CONTRACT } from 'src/utils/constants/app-routes-name'

const maxFilesAccepted = process.env.MAX_IMAGES_ACCEPTED
const { t } = i18n().global
let banned = [];

let bannedWordExist = false
const importOrder = ['Upright', 'Normal', 'Italic', 'Oblique', 'Slanted', 'Inclined', 'Cursive']
const designOrder = ['Serif', 'Sans', 'Slab', 'Micro', 'Text', 'Script', 'Headline', 'Display', 'Rounded', 'Monospaced', 'Stencil', 'Shadow', 'Typewriter', 'SmallText', 'Alternate', 'Caption', 'Dotted', 'Haar', 'Brush', 'AlternateScript', 'Poster', 'OldStyleFigures', 'Embossed', 'Negative', 'Outline', 'PetiteCaps', 'Reversed', 'Inline', 'Titling', 'Textbook', 'Subhead', 'Banner', 'Unicase', 'TallCaps', 'Trim', 'Expert', 'SmallCaps', 'Initials', 'Fraction', 'Capitals', 'Ligatures', 'TabularFigures', 'Swash', 'Ornaments', 'Engraved', 'Compact', 'Inserat', 'Blackletter', 'LiningFigures']
let warningWordExist = false
let previousWarningWordsRemoved = false
const bannedWordsString = window.localStorage.getItem('banned_words');
const bannedWords = bannedWordsString ? JSON.parse(bannedWordsString) : {};
const {
  TagRestrictedDesignerNames: designerNamesList,
  TagRestrictedFontFormats: fontFormatsList,
  TagRestrictedFoundryNames: foundryNamesList,
  TagRestrictedTautology: tautologyList,
  TagRestrictedTypefaceNames: typefaceNamesList,
  TagRestrictedWords: restrictedWordsList,
  Conjunctions: conjunctionsList
} = bannedWords;
const removeWords = restrictedWordsList || []
const fontFormat = fontFormatsList || []
const famousTypeDesigner = designerNamesList || []
const foundryName = foundryNamesList || []
const typefaces = typefaceNamesList || []
const tautologyWords = tautologyList || []
const conjunctionWords = conjunctionsList || []
let listOfSpecialCharacters = []
const FOUNDRY_NAME_INVALID_PATTERN = /[-/`~!#*$@_%+=^(){}[\]|;:"<>?\\]/
const SSN_INVALID_PATTERN = /^([0-9]{3}-[0-9]{2}-[0-9]{4})$/
import { adobeCjaEventEmitter } from '../../../../modules/adobe-cja/utils';

/**
 * Escapes CSV values to handle commas, quotes, and newlines
 * @param {*} value - Value to escape
 * @returns {String} - Escaped CSV value
 */
const csvEscape = (value) => {
  const str = value == null ? '' : String(value);
  if (/[",\n]/.test(str)) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
};

/**
 * Gets material number for a binary by matching format IDs with packages
 * @param {Object} binary - Binary object
 * @param {Array} packages - Array of package objects
 * @returns {String} - Material number or empty string
 */
const getMaterialNumberForBinary = (binary, packages) => {
  if (!Array.isArray(packages)) return '';

  // Filter packages to only include singleStyle packages (or packages without packageType for backward compatibility)
  const singleStylePackages = packages.filter(pkg =>
    pkg?.packageType === 'singleStyle' || pkg?.packageType === undefined
  );

  // First try to match by binaryId (most reliable)
  for (const pkg of singleStylePackages) {
    const styles = Array.isArray(pkg?.styles) ? pkg.styles : [];
    for (const style of styles) {
      if (style?.binaryId && style.binaryId === binary?.binaryId) {
        return pkg?.materialNumber || '';
      }
    }
  }

  // Fallback to format matching if binaryId matching fails
  const binaryFormatIds = Array.isArray(binary?.format)
    ? binary.format.map(fmt => fmt?.formatId).filter(id => id != null)
    : [];

  if (binaryFormatIds.length === 0) return '';

  for (const pkg of singleStylePackages) {
    const styles = Array.isArray(pkg?.styles) ? pkg.styles : [];
    for (const style of styles) {
      const styleFormatIds = Array.isArray(style?.format)
        ? style.format.map(fmt => fmt?.formatId).filter(id => id != null)
        : [];
      if (styleFormatIds.some(id => binaryFormatIds.includes(id))) {
        return pkg?.materialNumber || '';
      }
    }
  }
  return '';
};

/**
 * Gets single style package for a binary by matching format IDs or binary ID
 * @param {Object} binary - Binary object
 * @param {Array} packages - Array of package objects
 * @returns {Object|null} - Matching package or null
 */
const getSingleStylePackageByBinary = (binary, packages) => {
  if (!Array.isArray(packages)) return null;

  // Filter packages to only include singleStyle packages (or packages without packageType for backward compatibility)
  const singleStylePackages = packages.filter(pkg =>
    pkg?.packageType === 'singleStyle' || pkg?.packageType === undefined
  );

  // Prefer matching by binaryId, fallback to format id if present in styles
  for (const pkg of singleStylePackages) {
    const styles = Array.isArray(pkg?.styles) ? pkg.styles : [];
    for (const style of styles) {
      // First try to match by binaryId (most reliable)
      if (style?.binaryId && style.binaryId === binary?.binaryId) {
        return pkg;
      }
    }
  }

  // Fallback to format matching if binaryId matching fails
  const binaryFormatIds = Array.isArray(binary?.format)
    ? binary.format.map(fmt => fmt?.formatId).filter(id => id != null)
    : [];

  for (const pkg of singleStylePackages) {
    const styles = Array.isArray(pkg?.styles) ? pkg.styles : [];
    for (const style of styles) {
      const styleFormatIds = Array.isArray(style?.format)
        ? style.format.map(fmt => fmt?.formatId).filter(id => id != null)
        : [];
      const matchesFormat = binaryFormatIds.length > 0 && styleFormatIds.some(id => binaryFormatIds.includes(id));
      if (matchesFormat) {
        return pkg;
      }
    }
  }

  return null;
};

const capitalizeFirstLetter = (value) => {
  const str = value != null ? String(value).trim() : '';
  if (str === '') return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Case-insensitive string equality after trim (MYFC-46933: PS/style/family name matching).
 */
const isEqualIgnoreCase = (a, b) => {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
};

/**
 * Builds CSV string for inventory ingestion data
 * @param {Object} ingestionData - Ingestion data object
 * @param {String} param - Parameter to determine CSV structure ('live' or 'in_draft')
 * @returns {String} - CSV string with BOM
 */
const buildIngestionCSV = (ingestionData, param = 'live') => {
  const ingestion = ingestionData || {};
  const meta = ingestion.metadata || {};
  const owner = ingestion.owner || {};
  const submissions = Array.isArray(ingestion.submissions) ? ingestion.submissions : [];

  const rows = [];
  
  // Only include the top 16 rows (project metadata section) if param is 'live'
  if (param === 'live') {
    // Project metadata title row
    rows.push(['PROJECT METADATA', '—' ]);
    // Metadata header row
    rows.push(['Field', 'Value']);
    rows.push(['Project Name', meta.projectName || ingestion.projectName || '—']);
    rows.push(['Project Category', meta.type || '—']);
    rows.push(['Foundry', ingestion?.foundry?.name || '—']);
    rows.push(['Upload Project ID', ingestion?.id || '—']);
    rows.push(['MID', meta.mId || '—']);
    rows.push(['Number of Families', meta.totalFamilies != null ? meta.totalFamilies : '—']);
    rows.push(['Font Files', meta.totalFonts != null ? meta.totalFonts : '—']);
    rows.push(['Styles', meta.totalStyles != null ? meta.totalStyles : '—']);
    rows.push(['Upload Timestamp', ingestion.updatedAt || '—']);
    rows.push(['Uploaded by', owner.displayName || owner.email || '—']);
    rows.push(['Status', ingestion.status || '']);
    const uploadNote = ingestion?.ingestionNotes ? ingestion.ingestionNotes[0]?.note.replace(/<[^>]*>/g, '').trim() || '—' : '—';
    rows.push(['Upload Note', uploadNote]);

    // Blank line
    rows.push(['']);
  }

  // Families section header and columns
  rows.push(['FAMILIES']);
  rows.push([
    'Display Family Name',
    'Family Name',
    'Style name',
    'SKU Name',
    'Sort order',
    'Status',
    'Contract No',
    'Family price in $',
    'Style price in $',
    'Default style',
    'Designer names',
    'Family note',
    'Version',
    'Filename',
    'Format',
    'Related families',
    // Code will remove after sometime as it may need to put back again later
    /*'Number files',
    'Typeface description',
    'Font family tags',
    'Images',
    'PIM Family Id',
    'Material number',*/
  ]);

  submissions.forEach((submission) => {
    const umd = submission?.revisionDetails?.userMetaData || {};
    const binaries = Array.isArray(umd?.binaries) ? umd.binaries : [];
    const packages = Array.isArray(umd?.packages) ? umd.packages : [];

    const familyName = submission?.familyName || '—';
    const displayFamilyName = submission?.displayFamilyName || '—';
    const numberFiles = binaries.length;
    const familyPrice = umd?.familyPriceUsd != null ? umd.familyPriceUsd : '—';
    const designerNames = Array.isArray(umd?.designers) ? umd.designers.map(d => d?.name).filter(Boolean).join(', ') || '—' : '—';
    const typefaceDescription = Array.isArray(umd?.familyDescriptions) ? (umd.familyDescriptions[0]?.description || '—') : '—';
    // Check for familyGroup key and handle families array
    let relatedFamilies = '—';
    if (umd?.familyGroup && Array.isArray(umd.familyGroup.families) && umd.familyGroup.families.length > 0) {
      const filteredNames = umd.familyGroup.families
        .map(family => family?.familyName)
        .filter(name => name && name.trim() !== '');
      relatedFamilies = filteredNames.length > 0 ? filteredNames.join(', ') : '—';
    }
    const fontFamilyTags = Array.isArray(umd?.tags)
      ? umd.tags.map(tag => tag?.name).filter(Boolean).join(', ') || '—'
      : '—';
    const defaultStyle = umd?.defaultLayoutFont?.name || '—';
    const images = Array.isArray(umd?.imageSortorders) ? umd.imageSortorders.reduce((acc, cur) => acc + (Array.isArray(cur?.imageIds) ? cur.imageIds.length : 0), 0) : '—';
    // Extract and clean family note, removing HTML tags
    const rawFamilyNote = Array.isArray(umd?.familyNotes) ? (umd.familyNotes[0]?.note || '') : '';
    const familyNote = rawFamilyNote ? rawFamilyNote.replace(/<[^>]*>/g, '').trim() || '—' : '—';
    const pimFamilyId = submission?.pimFamilyId || '—';

    // Initialize sort order tracking variables
    let currentSortOrder = 0;
    let previousStyleName = null;

    binaries.forEach((binary) => {
      const singleStylePkg = getSingleStylePackageByBinary(binary, packages);
      const materialNumber = singleStylePkg?.materialNumber || '';
      const skuName = singleStylePkg?.name || '—';
      const stylePrice = singleStylePkg?.prices?.[0]?.price ?? '—';
      const styleName = binary?.styleName || '—';
      
      // Sort order logic: increment by 1, but keep same order if style name is same as previous (case-insensitive)
      const sortOrder = previousStyleName !== null && isEqualIgnoreCase(styleName, previousStyleName)
        ? currentSortOrder
        : ++currentSortOrder;
      previousStyleName = styleName;
      
      const contractNo = Array.isArray(binary?.contractNumber) ? binary.contractNumber.join(', ') : '—';
      const filename = binary?.fileName || '—';
      const version = binary?.fontInfo?.versionText || '—';
      const format = Array.isArray(binary?.format) ? (binary.format[0]?.name || '—') : '—';
      const status = (binary?.status != null && String(binary.status).trim() !== '') ? capitalizeFirstLetter(binary.status) : (submission?.isEdit ? 'Existing' : 'New') || '—';

      rows.push([
        displayFamilyName,
        familyName,
        styleName,
        skuName,
        sortOrder,
        status,
        contractNo,
        familyPrice,
        stylePrice,
        defaultStyle,
        designerNames,
        familyNote,
        version,
        filename,
        format,
        relatedFamilies,
        // Code will remove after sometime as it may need to put back again later
        /*numberFiles,
        typefaceDescription,
        fontFamilyTags,
        images,
        pimFamilyId,
        materialNumber,*/
      ].map(csvEscape));
    });
  });

  const csvContent = rows.map(row => (Array.isArray(row) ? row.join(',') : csvEscape(row))).join('\n');
  const bom = '\uFEFF';
  return bom + csvContent;
};

export default {
  /**
   * string capitalization - first letter - capital, other - lowercase.
   * @param {String} word - Word or sentence.
   */
  capitalize(word) {
    return `${word.slice(0, 1).toUpperCase()}${word.slice(1).toLowerCase()}`
  },
  /**
  * Convert a string to Title Case.
  * @param {String} text - The input string.
  * @returns {String} - The string in Title Case.
  */
  toTitleCase(text) {
    return text.split(' ').map(word => this.capitalize(word)).join(' ')
  },
  /**
   * String camelization - underscores, dividing words replaces to Capital letters
   * sound-text => soundText.
   * @param {String} text - Word or sentence
   * @param {String} separator - delimiter, '-' by default
   */
  camelize(text, separator = '-') {
    const words = text.split(separator)
    return [words[0], words.slice(1).map(word => this.capitalize(word))].join('')
  },
  /**
   * format date.
   * @param {String} utcDate - utc time.
   * @param {String} locale - lang iso, i.e. en for Localization
   * @param {String} format - see dayjs documentation
   */
  formatDate(utcDate, locale, format = 'LL') {
    dayjs.locale(locale)
    const timeZone = dayjs.tz.guess()
    return dayjs(utcDate).tz(timeZone).format(format)
  },
  /**
   * format date.
   * @param {String} utcDate - utc time.
   * @param {String} locale - lang iso, i.e. en for Localization
   * @param {String} format - see dayjs documentation
   */
  UTCFormatDate(utcDate, locale, format = 'LL') {
    const date = new Date(utcDate)
    dayjs.locale(locale)
    return dayjs.utc(date).format(format)
  },
  /**
   * format date.
   * @param {String} utcDate - utc time.
   * @param {String} locale - lang iso, i.e. en for Localization
   * @param {String} format - see dayjs documentation
   */
  dateFromNow(utcDate, locale, format = Common.DATE_DEFAULT_FORMAT) {
    const date = new Date(utcDate)
    return dayjs(date).locale(locale).fromNow()
  },
  // Cognitive Complexity exists in this function, need to refactor
  formattedDateFromNow(utcDate, locale = process.env.VUE_APP_DEFAULT_LOCALE, format = Common.DATE_DEFAULT_FORMAT) {
    let returnString = 'in '
    const endDate = dayjs(utcDate).locale(locale);
    const currentDate = dayjs().format('YYYY-MM-DD')
    let duration = this.getDays(endDate, currentDate)
    if (duration > 0) {
      const years = Math.floor(duration / 365)
      if (years > 0) {
        duration = duration - 365 * years
        returnString += `${years} year${years !== 1 ? 's' : ''}` + ' '
      }
      const months = Math.floor(duration / 30)
      if (months > 0) {
        returnString += `${months} month${months !== 1 ? 's' : ''}` + ' '
      }
      const days = duration % 30
      if (days > 0) {
        returnString += `${days} day${days !== 1 ? 's' : ''}`
      }
    } else {
      dayjs.locale(locale)
      return dayjs(utcDate).fromNow()
    }
    return returnString
  },
  /**
   * Is Future Date
   * @param {String} utcDate - utc time.
   */
  isFutureDate(utcDate) {
    const currentDate = dayjs().format()
    return dayjs(utcDate).isAfter(currentDate)
  },

  /**
   * Is Today's Date
   * @param {String} utcDate - utc time.
   */
  isTodaysDate(utcDate) {
    const currentDate = dayjs().format()
    return dayjs(utcDate).isSame(currentDate, 'date')
  },

  /**
   * Is Both Dates are same
   * @param {String} dateOne - date one.
   * @param {String} dateTwo - date two.
   */
  isBothDateSame(dateOne, dateTwo) {
    return dayjs(dateOne).isSame(dateTwo, 'date')
  },

  /**
   * Get the difference of seconds from today
   * @param {String} utcDate - utc time.
   */
  getDiffOfSecondsFromToday(utcDate) {
    const currentDate = dayjs().format()
    return dayjs(utcDate).diff(currentDate, 'seconds')
  },
  /**
   * format date.
   * @param {String} utcDate - utc time.
   * @param {String} locale - lang iso, i.e. en for Localization
   * @param {String} format - see dayjs documentation
   */
  formatUTCtoEST(utcDate, locale, format = 'L') {
    const defaultTimezone = Common.DEFAULT_TIMEZONE_FORMAT;
    dayjs.locale(locale)
    return dayjs(utcDate).tz(defaultTimezone).format(format)
  },

  /**
   * format date.
   * @param {String} firstDate - firstDate
   * @param {String} anotherDate - anotherDate
   * @returns {Boolean} format - see dayjs documentation
   */
  checkDateisAfterAnotherDate(firstDate, anotherDate) {
    return dayjs(firstDate).isAfter(anotherDate) || dayjs(firstDate).isSame(anotherDate)
  },
  /**
   * Upcoming Payment Date
   * If current date is between 1st to 15th of month (including 15th) then next payment date will be 15th of that month.
   * If current date is beyond 15th of the month then next payment date will be 15th of next month.
   * If correct date coming from API then compare with current date and return the date.
   * @returns {string}
   */
  getPaymentDate(locale, upcomingDate) {
    const defaultTimezone = Common.DEFAULT_TIMEZONE_FORMAT;
    const currentDate = Number(dayjs().tz(defaultTimezone).clone().locale(locale).format('DD'))
    const currentYear = dayjs().tz(defaultTimezone).clone().locale(locale).format('YYYY')
    const nextYear = dayjs().tz(defaultTimezone).clone().add(1, 'years').locale(locale).format('YYYY')
    const currentMonth = dayjs().tz(defaultTimezone).clone().locale(locale).format('MMMM')
    const isLastMonthOfYear = isEqual(currentMonth, 'December')
    const nextMonth = dayjs().tz(defaultTimezone).clone().add(1, 'months').locale(locale).format('MMMM')
    if (upcomingDate && dayjs(upcomingDate).isAfter(dayjs().tz(defaultTimezone).clone().locale(locale))) {
      return this.UTCFormatDate(upcomingDate, locale, 'MMMM DD, YYYY')
    }

    return currentDate <= 15
      ? `${currentMonth} 15, ${currentYear}`
      : `${nextMonth} 15, ${isLastMonthOfYear ? nextYear : currentYear}`
  },
  /*
  Trim Spaces
  */
  trimString(dataString) {
    return dataString.trim()
  },

  /*
  Only allow numberic character, in numeric input field
  */
  handleInput(event) {
    const key = event.key;
    const isNumber = /^[0-9]$/.test(key);
    const allowedKeys = Common.ALLOWED_KEYS;
    const inputValue = event?.target?.value;
    if (key === '.' && inputValue.includes('.')) {
      event.preventDefault();
    }
    const isShortcut =
      (event.metaKey || event.ctrlKey) &&
      ['a', 'c', 'x', 'z'].includes(key.toLowerCase()); // Allow select all, copy, paste, cut and revert
    if (!isNumber && !allowedKeys.includes(key) && !isShortcut) {
      event.preventDefault();
    }
  },
  /*
  function that handles the sorting of fonts 
  */
  defaultSort(resultList) {
    if (Object.keys(resultList)?.length > 0) {
      const results = { ...resultList }
      let uploadedFonts = Object.values(results)
      let fontTypeTTFV = []
      let fontTypeOther = []

      uploadedFonts.forEach((val) => {
        if (val.binariesData.fontInfo.formatFlavor === 'TTFV') {
          fontTypeTTFV.push(val)
        } else {
          fontTypeOther.push(val)
        }
      })
      fontTypeTTFV = this.defaultSortOrder(fontTypeTTFV)
      fontTypeOther = this.defaultSortOrder(fontTypeOther)
      const sortedFonts = [...fontTypeTTFV, ...fontTypeOther]

      let sortedResults = { items: flattenDeep(sortedFonts), timer: TimeoutUtil() }
      return sortedResults.items
    }
  },
  defaultSortOrder(uploadFonts) {
    let z = uploadFonts.sort((a, b) => (importOrder.indexOf(a.binariesData?.fontInfo?.slope) - importOrder.indexOf(b.binariesData?.fontInfo?.slope))).sort((a, b) => a.binariesData?.fontInfo?.weightClass - b.binariesData?.fontInfo?.weightClass).sort((a, b) => a.binariesData?.fontInfo?.widthClass - b.binariesData?.fontInfo?.widthClass).sort((a, b) => (designOrder.indexOf(a.binariesData?.fontInfo?.design) - designOrder.indexOf(b.binariesData?.fontInfo?.design)))
    return z
  },
  /*
  Replace spaces with dash and trim
  */
  dashedString(spacedString, delimiter = '-') {
    const normalizedString = spacedString.replace(/\s+/g, ' ');
    const trimTheString = this.trimString(normalizedString)
    const updatedString = trimTheString.replace(/ \s*/g, delimiter)
    return updatedString
  },
  /*
  Replace spaces with dash
  */
  formatPriceValue(price) {
    price = parseFloat(price);
    if (price % 1 === 0) {
      return price.toFixed(2);
    } else {
      return price.toString();
    }
  },
  spaceTodashString(spacedString, delimiter = '-') {
    const updatedString = spacedString.replace(/ \s*/g, delimiter)
    return updatedString
  },
  /*
  string To Lower Case
  */
  strToLowerCase(str = '') {
    return str.toLowerCase()
  },
  concatStr(str, newStr) {
    const label = str === undefined ? '"' + newStr + '"' : str + ', "' + newStr + '"'
    return label
  },
  getDuplicates(obj) {
    return _(obj)
      .groupBy()
      .pickBy(x => x.length > 1)
      .keys()
      .value()
  },
  removeDuplicates(data, obj = false) {
    if (obj) {
      return uniqWith(data, isEqual)
    }
    return uniq(data)
  },
  validateSize(string, length) {
    const isASCII = data => /^[\x00-\x7F]*$/.test(data)
    string = string.trim().replace(/\s+/g, ' ');
    let count = 0
    for (let i = 0; i < string.length; i++) {
      if (isASCII(string.charAt(i))) {
        count += 1
      } else {
        count += 2
      }
    }
    return count <= length
  },
  validateTagSize(string, length) {
    string = string.trim().replace(/\s+/g, ' ');
    const count = string.length;
    return count <= length;
  },
  findIndex(array, obj) {
    return findIndex(array, obj)
  },
  replaceString(original, edit, newString) {
    return original.replace(edit, newString)
  },
  getNewArrayForLive(array) {
    const data = array.map(val => {
      return {
        binaryId: val.id,
        psName: (val.fontInfo || 'n/a').psName || 'n/a',
        fileName: val.fileName,
        styleName: val.styleName || ''
      }
    })
    return data
  },
  mapperObject(bId, psName, filename, styleName, releaseData) {
    return {
      newBinary: {
        id: bId,
        psName,
        fileName: filename,
        styleName
      },
      releasedBinaries: releaseData
    }
  },
  strikeThroughBadWords(description, store, fieldName, module, foundryId, previousWarningWords = [], previousBannedWords = [], tagsAccepted = false, alltags) {
    let descriptionIs = description
    warningWordExist = false
    bannedWordExist = false
    descriptionIs = descriptionIs.replace(/<\/?s>/g, '')
    banned = []
    previousWarningWordsRemoved = false;
    let warningWordMatches;

    const bannedWordsString = window.localStorage.getItem('banned_words');
    const bannedWords = JSON.parse(bannedWordsString);
    const warningWords = bannedWords ? bannedWords.WarningWords : [];
    const disallowedWords = bannedWords ? bannedWords.DisallowedWords : [];

    if (isEmpty(warningWords) && isEmpty(disallowedWords)) {
      return { warningWordExist, warningWordMatches: [], bannedWordExist, description: descriptionIs, previousWarningWordsRemoved, banned };
    }
    if (!isEmpty(warningWords)) {
      const warningWordsPattern = `(?<![\\w$])(${warningWords.map(word => XRegExp.escape(word)).join('|')})(?![\\w$])`;
      if (tagsAccepted) {
        const normalizedWarningWords = warningWords.map(word => word.toLowerCase());
        const matchedTags = alltags.filter(tag => {
          const wordsInTag = tag.toLowerCase().split(/\s+/);
          return wordsInTag.some(word => normalizedWarningWords.includes(word));
        });

        if (matchedTags.length > 0) {
          warningWordMatches = matchedTags;
        }
      } else {
        const warningWordsRegex = XRegExp(warningWordsPattern, 'gi');
        warningWordMatches = XRegExp.match(descriptionIs, warningWordsRegex);
        warningWordMatches = [...new Set(warningWordMatches)];
      }
      if (warningWordMatches?.length > 0) {
        warningWordExist = true;
      }
    }

    if (!isEmpty(disallowedWords)) {
      const disallowedWordPattern = `(?<![\\w$])(${disallowedWords.map(word => XRegExp.escape(word)).join('|')})(?![\\w$])`;
      const regex = XRegExp(disallowedWordPattern, 'gi');
      const matches = XRegExp.match(descriptionIs, regex);
      // Check if the description contains banned words
      if (matches.length === 0) {
        descriptionIs = descriptionIs.replace(/<s\b[^>]*>(.*?)<\/s>/g, '<span>$1</span>')
        description = descriptionIs
        store.commit('bannedword/setHasBannedWord', bannedWordExist)
        if ((previousBannedWords.length > 0 || previousWarningWords.length > 0) && (!warningWordExist && !bannedWordExist)) {
          Toaster.notifySuccess(t('pages.profile.foundry_profile.warning_error_removed'));
        }
        return { warningWordExist, warningWordMatches, bannedWordExist, description, banned };
      }
      if (matches.length > 0) {
        descriptionIs = descriptionIs.replace(/<pre><code>(.*?)<\/code><\/pre>/gs, (match, content) => {
          return `<p>${content}</p>`;
        });
        description = descriptionIs.replace(regex, match => {
          banned.push(match);
          return `<s>${match}</s>`;
        });
        if (banned.length > 0) {
          bannedWordExist = true;
          store.dispatch('bannedword/saveBannedWordsToBackend', {
            module: module,
            fieldName: fieldName,
            bannedWords: banned,
            foundryId: foundryId
          })
          store.commit('bannedword/setHasBannedWord', bannedWordExist);
        }
      }
    }
    if ((previousBannedWords.length > 0 || previousWarningWords.length > 0) && (!warningWordExist && !bannedWordExist)) {
      Toaster.notifySuccess(t('pages.profile.foundry_profile.warning_error_removed'));
    }
    return { warningWordExist, warningWordMatches, bannedWordExist, description, banned };
  },

  capitalizeWord(str) {
    const conjunctionMap = conjunctionWords?.reduce((map, phrase) => {
      const placeholder = phrase.replace(/\s+/g, '-');
      map[placeholder] = phrase;
      return map;
    }, {});

    let tagWithPlaceholders = str;
    Object.keys(conjunctionMap).forEach(placeholder => {
      const phrase = conjunctionMap[placeholder];
      const regex = new RegExp(`\\b${phrase}\\b`, 'gi');
      tagWithPlaceholders = tagWithPlaceholders.replace(regex, placeholder);
    });

    let words = tagWithPlaceholders.split(' ').map((word, index) => {
      const originalWord = conjunctionMap[word] || word;
      if (index === 0 && Object.values(conjunctionMap).includes(originalWord.toLowerCase())) {
        return originalWord.toLowerCase();
      }
      if (index === 0 || !Object.values(conjunctionMap).includes(originalWord.toLowerCase())) {
        return originalWord.charAt(0).toUpperCase() + originalWord.slice(1);
      }

      return originalWord.toLowerCase();
    }).join(' ');

    return words;
  },

  handleInvalidTagValue(tagName) {
    listOfSpecialCharacters = [];
    if (/[^a-zA-Z0-9\p{L}\p{M}\u0020\u00A0'’-]/gu.test(tagName) || /^['\-\’]+$/g.test(tagName)) {
      listOfSpecialCharacters = tagName.match(/[^a-zA-Z0-9\p{L}\p{M}'’-]/gu) || tagName.match(/^['’\-]+$/gu);
      listOfSpecialCharacters = [...new Set(listOfSpecialCharacters)];
    }
    return listOfSpecialCharacters;
  },

  checkForStopwordsInTag(tagName) {
    const cleanedTagName = tagName.trim().replace(/\s+/g, ' ');
    const tagWords = cleanedTagName.split(/\s+/);
    const stopWords = removeWords.map(word => word.trim().replace(/\s+/g, ' ').toLowerCase());
    const filteredWords = tagWords.filter(word => !stopWords.includes(word.toLowerCase()));
    if (filteredWords.length === 0) {
      return false;
    }
    if (/^\d{4}$/.test(filteredWords) && (parseInt(filteredWords) < 1800 || parseInt(filteredWords) > 2099)) {
      return false;
    }
    if (/^\d{4}$/.test(filteredWords) && parseInt(filteredWords) >= 1800 && parseInt(filteredWords) <= 2099 && (parseInt(filteredWords) % 10 === 0)) {
      return filteredWords + 's';
    }
    return filteredWords.join(' ');
  },
  validateTagValue(tagName, invalidWordsMap) {
    let validTagName;
    Object.entries(invalidWordsMap).forEach(([type, words]) => {
      if (!Array.isArray(words)) {
        words = [words];
      }
      invalidWordsMap[type] = words.map(word => word.trim().replace(/\s+/g, ' ').toLowerCase());
    });
    let cleanedTagName = tagName.trim().replace(/\s+/g, ' ').toLowerCase();

    for (const words of Object.values(invalidWordsMap)) {
      if (words.includes(cleanedTagName)) {
        return false;
      }
    }
    validTagName = this.checkForStopwordsInTag(tagName);
    return validTagName;
  },
  tagValidations(tag) {
    const invalidWordsMap = {
      fontFormat: fontFormat,
      famousTypeDesigner: famousTypeDesigner,
      typefaces: typefaces,
      foundryName: foundryName,
      tautology: tautologyWords,
    }
    const validatedTagName = this.validateTagValue(tag, invalidWordsMap);
    const finalTagName = typeof validatedTagName === 'string' ? this.validateTagValue(validatedTagName, invalidWordsMap) : validatedTagName;
    return finalTagName
  },
  getDiffArrayFromAll(allData, addedData) {
    return allData.filter(item => !addedData.some(other => item.binaryId === other.binaryId))
  },
  getModalErrorObj(id, msg) {
    return {
      id,
      msg
    }
  },
  checkDuplicate(inputText, existingValues) {
    if (!inputText || !Array.isArray(existingValues)) return;
    const inputTextWords = inputText.split(',').map(word => word.trim().toLowerCase()).filter(Boolean);
    const existingValuesWords = existingValues
      .flatMap(value => value.split(',').map(word => word.trim().toLowerCase())).filter(Boolean);
    const isDuplicate = inputTextWords.some(word => existingValuesWords.includes(word));
    return isDuplicate;
  },
  isObjExists(obj, array) {
    // Obj  structure : { id: ..., msg: '.....'}
    return array.some(item => item.id === obj.id && item.msg === obj.msg)
  },
  moveIndexinArray(arr, oldIdx, newIdx) {
    while (oldIdx < 0) {
      oldIdx += arr.length
    }
    while (newIdx < 0) {
      newIdx += arr.length
    }
    if (newIdx >= arr.length) {
      let k = newIdx - arr.length
      while (k-- + 1) {
        arr.push(undefined)
      }
    }
    arr.splice(newIdx, 0, arr.splice(oldIdx, 1)[0])
  },
  getPackageDetails(array) {
    const packageArray = array.map(val => {
      return {
        externalSku: val.externalSku,
        name: val.name,
        packageType: val.packageType,
        prices: val.prices,
        styles: val.styles
      }
    })
    return packageArray
  },
  HTTPStartString(val) {
    if (!/^https?:/i.test(val)) {
      val = 'http://' + val
    }
    return val
  },
  HTTPinString(str) {
    const newStr = str.replace(/(href=")(?!(https|http))/g, 'href="http://')
    return newStr
  },
  /**
   * Custom Validation for required
   * when user enters only one or more spaces in tag <p> </p>
   * its treated as a character,
   * need to treat as empty string
   */
  sanitizeHTML(str) {
    if (str.length !== undefined) {
      const newStr = str.replace(/<p[^>]*>|<\/p[^>]*>|<br[^>]*>/gi, '')
      return newStr.trim() === ''
    }
  },
  binaryArrayError(arr, self) {
    const isAvailable = arr.some(item => Array.isArray(item))
    if (isAvailable) {
      return self.$t('pages.submissions.review.approve.error_msg.binary_error')
    }
    return []
  },
  findSpecificObject(arr, searchObj) {
    return find(arr, searchObj)
  },
  getSpecificDate(enterDate, period) {
    if (enterDate.length > 0) {
      const date = new Date(enterDate)
      date.setDate(date.getDate() + Number(period))
      return date.toISOString().slice(0, 10)
    }
    return ''
  },
  getDays(dt1, dt2) {
    dayjs(new Date(dt1))
    const d1 = dayjs(new Date(dt1))
    const d2 = dayjs(new Date(dt2))
    return d1.diff(d2, 'days')
  },
  getAPIDateFormat(dt) {
    return new Date(dt).toISOString().slice(0, 19) + '+00:00'
  },
  setDataLayerObj(isUFDAFlag = false) {
    // Used to set data for Google Analytics in Vue App using native JS
    let dataLayerIndex = this.findIndex(window.dataLayer, item => item.hasOwnProperty('userID'))
    let name = window.localStorage.getItem('current_user')
    let displayName = window.localStorage.getItem('display_name')
    let userId = window.localStorage.getItem('userId')
    let foundry = window.localStorage.getItem('sessionFoundryName')
    let sessionFoundryId = window.localStorage.getItem('sessionFoundryId')
    let foundries = JSON.parse(localStorage.getItem('userFoundries'))
    let userRoleInStorage = window.localStorage.getItem('roles')
    let userRole = ''
    if (userRoleInStorage) {
      userRole = JSON.parse(userRoleInStorage)
    }
    let sessionFoundry = foundries?.find(function (item) {
      return item.id === sessionFoundryId
    })
    let contractSummary = sessionFoundry?.contractSummary
    let externalId = sessionFoundry?.externalId
    let pimContractSummary = sessionFoundry?.pimContractSummary
    let UFDA2 = contractSummary == 'UFDA 2'
    let dataLayerUserCreds = {
      userID: userId,
      Name: displayName,
      Email: name,
      Foundry: foundry,
      UFDA2,
      event: 'UserLogin',
      userRole: userRole[0],
      externalId,
      contractSummary,
      pimContractSummary
    }
    if (dataLayerIndex > -1) {
      window.dataLayer[dataLayerIndex] = dataLayerUserCreds
      window.dataLayer.push(dataLayerUserCreds)
    }
  },
  // Get session foundries contract summary
  getPimContractSummary() {
    let sessionFoundryId = window.localStorage.getItem('sessionFoundryId');
    let foundries = JSON.parse(localStorage.getItem('userFoundries'));

    if (sessionFoundryId && Array.isArray(foundries)) {
      let foundry = foundries.find(f => f.id === sessionFoundryId);
      return foundry?.pimContractSummary || null;
    }
    return null;
  },
  getDifferenceBy(list1, list2, property) {
    return differenceBy(list1, list2, property)
  },
  validateFirstAlphaCaps(str) {
    const firstChar = str.trim().substr(0, 1)
    return firstChar === firstChar.toUpperCase()
  },
  validateAllCaps(str) {
    let newString = str
      .trim()
      .split(' ')
      .filter(x => x)
      .join('')
    newString = newString.replace(/[^a-zA-Z ]/g, '')
    if (isNaN(Number(newString))) {
      return (newString !== newString.toUpperCase())
    }
    return true
  },
  validateCustomRegex(str, pattern = /^[0-9a-zA-Z ]+$/) {
    // Default Alphabet, number, space
    return pattern.test(str)
  },
  normalizeDiacriticsOnChar(char) {
    let accent =
      'áâàäåãÂÀÄÅÃāĀÂçÇćĆčČéêèëÉÊÈËēėęĒĖĘíîìïıÍÎÌÏįĮŹźẐẑŽžŻżẒẓẔẕƵƶȤȥⱫⱬⱿɀᵶʐʑóôòöõøōÓÔÒÖÕØŌñÑńŃŠšśŚÝŸýÿúûùüÛÙÜÚūŪ'
    let output =
      'aaaaaaaaaaaaaacccccceeeeeeeeeeeeeeiiiiiiiiiiizzzzzzzzzzzzzzzzzzzzzzzoooooooooooooonnnnssssyyyyuuuuuuuuuu'
    accent = accent.split('')
    accent.push('&', 'æ', 'Æ', 'œ', 'Œ', 'ß', 'ẞ')
    output = output.split('')
    output.push('and', 'ae', 'ae', 'oe', 'oe', 'ss', 'ss')
    const obj = {}
    for (let i = 0; i < accent.length; i++) {
      obj[accent[i]] = output[i]
    }
    return obj[char] ? obj[char] : char
  },
  normalizeDiacriticsOnString(str = '') {
    if (str.length === 1) {
      return this.normalizeDiacriticsOnChar(str)
    }
    if (str.length > 1) {
      const strArray = str.split('')
      const reducer = (accumulator, currentValue) =>
        this.normalizeDiacriticsOnChar(accumulator) + this.normalizeDiacriticsOnChar(currentValue)
      return strArray.reduce(reducer)
    }
  },
  validateScriptRegex(str, pattern = /^(?!.*(<script>|&lt;script&gt;|&lt;script)).*$/i) {
    const inputtedVal = String(str)
    return pattern.test(JSON.stringify(inputtedVal))
  },
  validateHtmlTags(str, pattern = /<[^>]*>|<|>/g) {
    const inputtedVal = String(str);
    return !pattern.test(inputtedVal);
  },
  customStringRTE(str) {
    const unsanitizedString = isEqual(typeof str, 'string') ? str : str.root.innerHTML
    const sanitizedStringFromHTML = unsanitizedString
      .trim()
      .replace(/(<([^>]+)>)/gi, '')
      .replace(/&amp;/g, '&')
    return sanitizedStringFromHTML.trim()
  },
  validateSpecialChars(str, pattern = /[-/`'~!#*$@_%+=.,^&(){}[\]|;:"<>?\\]/) {
    return pattern.test(str)
  },
  scriptErrorObj() {
    return {
      message: 'Request failed due to invalid entry',
      name: 'Dependency injection error',
      response: {
        data: {
          error: 'The entered value is invalid, please do not use script tag.'
        },
        status: 555
      },
      stack: 'DI error'
    }
  },
  removeDuplicateObjOnKey(data, key) {
    return [...new Map(data.map(item => [key(item), item])).values()]
  },
  setDecimalFixed(value) {
    return Number(Number(value).toFixed(3))
  },

  /**
   * Determines if given value is not undefined, null, or ''.
   * If the value is a number, it determines if it is not NaN.
   * @param {*} value value to test
   * @returns {boolean}
   */
  isValidValue(value) {
    return !(
      typeof value === 'undefined' ||
      value === null ||
      value === '' ||
      (typeof value === 'number' && Number.isNaN(value))
    )
  },

  /**
   * Determines whether ellipsis is Active or not
   * @param {object} e - selected DOM node
   * @returns {Boolean}
   */
  isEllipsisActive(e) {
    return e.offsetWidth < e.scrollWidth
  },

  /**
   * Scroll into the view by elements Name
   * @param {String} fieldName - field name where view to scrolled
   * @returns {Function}
   */
  scrollIntoViewByFieldName(fieldName) {
    document.getElementsByName(fieldName)[0]?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
    return document.getElementsByName(fieldName)?.[0].focus()
  },

  /**
   * Method to get current app environment
   * @returns {string}
   */
  getAppEnvironment() {
    const currentDomain = window.location.origin
    if (!this.isValidValue(currentDomain)) return 'production'

    switch (currentDomain) {
      case Common.PROD_DOMAIN: {
        return 'production'
      }
      case Common.PROD_EKS_DOMAIN: {
        return 'prod_eks'
      }
      case Common.PRE_PROD_DOMAIN: {
        return 'preprod'
      }
      case Common.UAT_DOMAIN: {
        return 'uat'
      }
      case Common.PRE_PROD_DOMAIN_CERBERUS: {
        return 'cer'
      }
      case Common.PRE_PROD_DOMAIN_CERBERUS_2: {
        return 'cer2'
      }
      case Common.PRE_PROD_DOMAIN_ALEXANDRIA: {
        return 'alex'
      }
      default: {
        return 'local'
      }
    }
  },
  /**
   * Method to get auth0 credential Details
   * @returns {Object}
   */
  getAuth0CredDetails() {
    switch (this.getAppEnvironment()) {
      case 'production': {
        return {
          auth0ClientId: 'OTXXdIsxljZfoU3Uh4cheKByLVCXPDCd',
          auth0DomainName: 'https://secure.monotype.com',
          auth0RedirectUri: 'https://fontplatform.monotype.com/login_page/',
          auth0LogoutRedirectUri: 'https://fontplatform.monotype.com/logout',
          auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform.monotype.com/login',
          accountsUrl: 'https://accounts.monotype.com/api/initLogin',
          MTFontsUrl: 'https://enterprise.monotype.com/auth/externallogin?providerKey=auth0&returnUrl=/en/dashboard',
          myFontsUrl: 'https://accounts.monotype.com/api/handleConsumerLogin?consumerType=myFonts&locale=en&finalPage=/',
          sigUrl: 'https://sig.monotype.com',
          mfpUrl: 'https://fontplatform.monotype.com'
        }
      }
      case 'prod_eks': {
        return {
          auth0ClientId: 'OTXXdIsxljZfoU3Uh4cheKByLVCXPDCd',
          auth0DomainName: 'https://secure.monotype.com',
          auth0RedirectUri: 'https://fontplatform-eks.monotype.com/login_page/',
          auth0LogoutRedirectUri: 'https://fontplatform-eks.monotype.com/logout',
          auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform-eks.monotype.com/login',
          accountsUrl: 'https://accounts.monotype.com/api/initLogin',
          MTFontsUrl: 'https://enterprise.monotype.com/auth/externallogin?providerKey=auth0&returnUrl=/en/dashboard',
          myFontsUrl: 'https://accounts.monotype.com/api/handleConsumerLogin?consumerType=myFonts&locale=en&finalPage=/',
          sigUrl: 'https://sig.monotype.com',
          mfpUrl: 'https://fontplatform-eks.monotype.com'
        }
      }
      case 'cer': {
        return {
          auth0ClientId: 'Fya8dLMhdcFED0CDOFhti2yZZHQjeAJT',
          auth0DomainName: 'https://secure-pp.monotype.com',
          auth0RedirectUri: 'https://fontplatform-eks-cerberus.monotype-dev.com/login_page',
          auth0LogoutRedirectUri: 'https://fontplatform-eks-cerberus.monotype-dev.com/logout',
          auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform-eks-cerberus.monotype-dev.com/login',
          accountsUrl: 'https://pp-accounts.monotype.com/api/initLogin',
          MTFontsUrl: 'https://enterprise-preprod.monotype.com/auth/externallogin?providerKey=auth0&returnUrl=/en/dashboard',
          myFontsUrl: 'https://pp-accounts.monotype.com/api/handleConsumerLogin?consumerType=myFonts&locale=en&finalPage=/',
          sigUrl: 'https://sig-preprod.monotype.com',
          mfpUrl: 'https://fontplatform-eks-cerberus.monotype-dev.com'
        }
      }
      case 'alex': {
        return {
          auth0ClientId: 'Fya8dLMhdcFED0CDOFhti2yZZHQjeAJT',
          auth0DomainName: 'https://secure-pp.monotype.com',
          auth0RedirectUri: 'https://fontplatform-eks-alex.monotype-dev.com/login_page',
          auth0LogoutRedirectUri: 'https://fontplatform-eks-alex.monotype-dev.com/logout',
          auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform-eks-alex.monotype-dev.com/login',
          accountsUrl: 'https://pp-accounts.monotype.com/api/initLogin',
          MTFontsUrl: 'https://enterprise-preprod.monotype.com/auth/externallogin?providerKey=auth0&returnUrl=/en/dashboard',
          myFontsUrl: 'https://pp-accounts.monotype.com/api/handleConsumerLogin?consumerType=myFonts&locale=en&finalPage=/',
          sigUrl: 'https://sig-preprod.monotype.com',
          mfpUrl: 'https://fontplatform-eks-alex.monotype-dev.com'
        }
      }
      case 'preprod': {
        return {
          auth0ClientId: 'Fya8dLMhdcFED0CDOFhti2yZZHQjeAJT',
          auth0DomainName: 'https://secure-pp.monotype.com',
          auth0RedirectUri: 'https://fontplatform-eks.monotype-pp.com/login_page',
          auth0LogoutRedirectUri: 'https://fontplatform-eks.monotype-pp.com/logout',
          auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform-eks.monotype-pp.com/login',
          accountsUrl: 'https://pp-accounts.monotype.com/api/initLogin',
          MTFontsUrl: 'https://enterprise-preprod.monotype.com/auth/externallogin?providerKey=auth0&returnUrl=/en/dashboard',
          myFontsUrl: 'https://pp-accounts.monotype.com/api/handleConsumerLogin?consumerType=myFonts&locale=en&finalPage=/',
          sigUrl: 'https://sig-preprod.monotype.com',
          mfpUrl: 'https://fontplatform-eks.monotype-pp.com'
        }
      }
      case 'uat': {
        return {
          auth0ClientId: 'AQQJuAKRWLTkUFPi19FIRCU25MYC8jmX',
          auth0DomainName: 'https://secure-pp.monotype.com',
          auth0RedirectUri: 'https://fontplatform-eks-qa.monotype-pp.com/login_page',
          auth0LogoutRedirectUri: 'https://fontplatform-eks-qa.monotype-pp.com/logout',
          auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform-eks-qa.monotype-pp.com/login',
          accountsUrl: 'https://pp-accounts.monotype.com/api/initLogin',
          MTFontsUrl: 'https://enterprise-preprod.monotype.com/auth/externallogin?providerKey=auth0&returnUrl=/en/dashboard',
          myFontsUrl: 'https://pp-accounts.monotype.com/api/handleConsumerLogin?consumerType=myFonts&locale=en&finalPage=/',
          sigUrl: 'https://sig-preprod.monotype.com',
          mfpUrl: 'https://fontplatform-eks-qa.monotype-pp.com'
        }
      }
      case 'cer2': {
        return {
          auth0ClientId: 'Fya8dLMhdcFED0CDOFhti2yZZHQjeAJT',
          auth0DomainName: 'https://secure-pp.monotype.com',
          auth0RedirectUri: 'https://fontplatform-eks-cerberus2.monotype-dev.com/login_page',
          auth0LogoutRedirectUri: 'https://fontplatform-eks-cerberus2.monotype-dev.com/logout',
          auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform-eks-cerberus2.monotype-dev.com/login',
          accountsUrl: 'https://pp-accounts.monotype.com/api/initLogin',
          MTFontsUrl: 'https://enterprise-preprod.monotype.com/auth/externallogin?providerKey=auth0&returnUrl=/en/dashboard',
          myFontsUrl: 'https://pp-accounts.monotype.com/api/handleConsumerLogin?consumerType=myFonts&locale=en&finalPage=/',
          sigUrl: 'https://sig-preprod.monotype.com',
          mfpUrl: 'https://fontplatform-eks-cerberus2.monotype-dev.com'
        }
      }
      default: {
        return {
          auth0ClientId: 'Ll9lH43YytzEU5ql1VU4eGep77Y3IZI9',
          auth0DomainName: 'https://secure-test.monotype.com',
          auth0RedirectUri: 'http://127.0.0.1/login_page',
          auth0LogoutRedirectUri: 'http://127.0.0.1/logout',
          auth0LogoutRedirectUriForUnauthorisedUser: 'http://127.0.0.1/login',
          accountsUrl: 'https://pp-accounts.monotype.com/api/initLogin',
          MTFontsUrl: 'https://enterprise-preprod.monotype.com/auth/externallogin?providerKey=auth0&returnUrl=/en/dashboard',
          myFontsUrl: 'https://pp-accounts.monotype.com/api/handleConsumerLogin?consumerType=myFonts&locale=en&finalPage=/',
          sigUrl: 'https://sig-preprod.monotype.com',
          mfpUrl: 'http://127.0.0.1'
        }
      }
    }
  },
  redirectToExternalCMSURL() {
    const externalCMSURL = this.getCMSLoginPageContentUrl()
    window.location.href = externalCMSURL
  },
  getCMSLoginPageContentUrl() {
    switch (this.getAppEnvironment()) {
      case 'production': {
        return 'https://fontplatform.monotype.com/content/welcome'
      }
      case 'prod_eks': {
        return 'https://fontplatform-eks.monotype.com/content/welcome'
      }
      case 'preprod': {
        return 'https://fontplatform-eks.monotype-pp.com/content/welcome'
      }
      case 'cer': {
        return 'https://fontplatform-eks.monotype-pp.com/content/welcome?ref=cer'
      }
      case 'cer2': {
        return 'https://fontplatform-eks.monotype-pp.com/content/welcome?ref=cer2'
      }
      case 'alex': {
        return 'https://fontplatform-eks.monotype-pp.com/content/welcome?ref=alex'
      }
      case 'uat': {
        return 'https://fontplatform-eks.monotype-pp.com/content/welcome?ref=uat'
      }
      default: {
        return 'https://fontplatform-eks.monotype-pp.com/content/welcome?ref=local'
      }
    }
  },
  getRoleIdMapping() {
    switch (this.getAppEnvironment()) {
      case 'production': 
      case 'prod_eks': {
        return {
          payment: 'rol_kvMzmVtaYF17fJb1',
          agreements: 'rol_fhXssqrz3Q7ojeT4',
          submissions: 'rol_sV8nXAGlHjRSpTVq',
          promotions: 'rol_eU42r45ioy7ClmhO',
          salesReport: 'rol_LmkUB88vp11YDrz7',
          foundryProfile: 'rol_ZknulK4GaMg3uCcJ',
          userManagement: 'rol_seKx5lLLmL62scJL',
          bundles: 'rol_KPK7Zu5o5usop49s'
        }
      }
      case 'preprod':
      case 'uat':
      case 'cer':
      case 'cer2':
      case 'preprod-8.2':
      case 'man':
      case 'man2':
      case 'alex':
        {
          return {
            payment: 'rol_BMWWEgKV58MLBrU6',
            agreements: 'rol_3mC26mQrm4EZ6wUv',
            submissions: 'rol_VrF66a3He3Q3R8R5',
            promotions: 'rol_tgvaxztzYh6yLdtr',
            salesReport: 'rol_OjArK43RzLVu5gVi',
            foundryProfile: 'rol_CdiiMbtOSCWXtnV7',
            userManagement: 'rol_T2nxZM7vmUwedjq9',
            bundles: 'rol_5T5E3x45s0TCakrf'
          }
        }
      case 'dha-uat':
      case 'dha':
        {
          return {
            payment: 'rol_BMWWEgKV58MLBrU6',
            agreements: 'rol_3mC26mQrm4EZ6wUv',
            submissions: 'rol_VrF66a3He3Q3R8R5',
            promotions: 'rol_tgvaxztzYh6yLdtr',
            salesReport: 'rol_OjArK43RzLVu5gVi',
            foundryProfile: 'rol_CdiiMbtOSCWXtnV7',
            userManagement: 'rol_T2nxZM7vmUwedjq9'
          }
        }
      default: {
        return {
          payment: 'rol_UHdtUUtpeZjSPogy',
          agreements: 'rol_uV6674P0u06i0Nnj',
          submissions: 'rol_D5U7UO2rcJudZGXX',
          promotions: 'rol_nXO4DXeOr1F8VwzL',
          salesReport: 'rol_TMF6v7JhuJjUTM1J',
          foundryProfile: 'rol_km64tpDqoei0LzpU',
          userManagement: 'rol_Kui1R6NwwePzyWRj',
          bundles: 'rol_hotgn2KLhRxZVKRy'
        }
      }
    }
  },
  formatCurrency(value, currencySymbol = '$') {
    const amount = parseFloat(value)

    if (isNaN(amount)) {
      return ''
    }

    const formattedAmount = amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })

    return `${currencySymbol} ${formattedAmount}`
  },
  /**
   * Format a price according to the rule: currency + Thinspace + number (comma for thousands, dot for pennies)
   * Example: $ 16,146,116.89
   * @param {String} amount - amount
   * @param {String} currencyType - currency Type eg: USD, INR
   * @returns {Number}
   */
  getAmount(amount, currencyType) {
    const validCurrencyType = currencyType && typeof currencyType === 'string' ? currencyType : 'USD'
    const symbol = new Intl.NumberFormat('en-US', { style: 'currency', currency: validCurrencyType }).formatToParts(1).find(p => p.type === 'currency')?.value || '$'
    const formatted = this.formatCurrency(amount, symbol)
    if (formatted) {
      return formatted
    }
    return this.formatCurrency(0, symbol)
  },
  /**
   * Format a price with currency, handling negatives, using getAmount for formatting.
   * @param {number|string} amount
   * @param {string} currencyCode
   * @returns {string}
   */
  formatPrice(amount, currencyCode = 'USD') {
    if (amount < 0) {
      return `-${this.getAmount(Math.abs(amount), currencyCode)}`
    }
    return this.getAmount(amount, currencyCode)
  },
  async generateJwtToken(payload) {
    try {
      const signedPayload = await Api.Jwt.signPayloadWithJwt({ data: payload })
      if (signedPayload?.data?.success && signedPayload?.data?.token) {
        return signedPayload.data.token
      } else {
        throw new Error('Invalid JWT token response')
      }
    } catch {
      setTimeout(() => {
        window.location.href = window.location.origin
      }, 2000)
      throw new Error('!Unable to generate secure payload for redirect, Try again later')
    }
  },
  async getJwtToken(payload = '') {
    const timeStamp = new Date().getTime()
    payload.iat = timeStamp
    let token = await this.generateJwtToken(payload)
    token = btoa(token)
    global.window.localStorage.setItem('jwt_token', token)
    return token
  },
  /**
   * Method to find permisions per user
   * @param {Array} foundriesArr
   * @returns {Array}
   */
  getPermissionsArr(foundriesArr) {
    const permissionArr = []
    foundriesArr?.forEach(data => data.permissions?.forEach(item => permissionArr.push(item.name)))
    return isEqual(permissionArr.length, 0) ? ['profile'] : [...new Set(permissionArr)]
  },

  updateSessionFoundryBasedOnPermissions(foundryId, foundryName, setSessionFoundry = true) {
    return dispatchSessionFoundryIfChanged(foundryId, foundryName, this.$store, setSessionFoundry)
  },
  /**
   * Method to find route after login
   * @param {Object} defaultFoundry
   * @param {Array} foundries
   * @param {String} tabName
   */
  setFoundryForModule(defaultFoundry, foundries, tabName, navFlag, rolesArr, logo) {
    if (rolesArr && rolesArr.includes('foundrySupport')) {
      return AppRoutesUrl.FOUNDRY_APPLICATIONS
    } else if (rolesArr?.includes('bulkIngestionUser')) {
      return AppRoutesUrl.INVENTORY_INGESTION_HUB
    } else if (rolesArr?.includes('dropShipUser')) {
      return AppRoutesUrl.DROPSHIP
    } else {
      return AppRoutesUrl.DASHBOARD
    }
  },

  checkPermissions(defaultFoundry, foundries, tabName, skip) {
    /** id mapping corresponds to tab roles-name */
    let rolesObj = this.getRoleIdMapping()
    let shouldSkip = false
    let foundryPermissionPresent = false
    let defaultFoundryPermissionPresent = false
    let foundryData = {}
    /** check for the module which has the permission for the default foundry */
    defaultFoundryPermissionPresent = defaultFoundry.permissions.some(per => per.id === rolesObj[tabName])
    if (defaultFoundryPermissionPresent) {
      this.updateSessionFoundryBasedOnPermissions(defaultFoundry.id, defaultFoundry.name)
      skip = true
    } else {
      /** check for the module which does not have permission for default foundry and set the permission from the next foundry permission available */
      foundries.forEach(foundry => {
        /** to prevent the foreach call for every foundry after the first match in foundry permissions */
        if (shouldSkip) {
          return
        }
        foundryPermissionPresent = foundry.permissions.some(p => p.id === rolesObj[tabName])
        if (foundryPermissionPresent) {
          foundryData = this.updateSessionFoundryBasedOnPermissions(foundry.id, foundry.name)
          shouldSkip = true
          skip = true
        }
      })
    }
    return { skip, tabName, foundryData }
  },

  /** this function will execute on click of navigation tab after foundries call */
  setFoundryForModuleOnNavigation(defaultFoundry, foundries, tabName, sessionFoundryId) {
    return resolveModuleSessionFoundry({
      defaultFoundry,
      foundries,
      tabName,
      sessionFoundryId,
      getRoleIdMapping: () => this.getRoleIdMapping(),
      updateSession: (id, name) => this.updateSessionFoundryBasedOnPermissions(id, name),
      router: this.$router
    })
  },

  /**
   * TODO! For now, we have modified the session foundry that is suitable for bundle module. It can be refactored into reusable function
   * @param {Object} defaultFoundry
   * @param {Array} foundries
   * @param {String} tabName
   * @param {String} sessionFoundryId
   * @returns {Object}
   */

  setSessionFoundryForBundleModuleOnNavigation(defaultFoundry, foundries, tabName, sessionFoundryId, setSessionFoundry = true) {
    const moduleRoleId = this.getRoleIdMapping()[tabName]
    let foundryData = {}
    const sessionFoundry = foundries.find(foundry => isEqual(foundry.id, sessionFoundryId))
    const needsFallback = shouldPickAnotherFoundry(
      defaultFoundry,
      foundries,
      sessionFoundryId,
      moduleRoleId,
      { keepSessionWithPermission: false }
    )

    if (
      sessionFoundry &&
      hasModulePermission(sessionFoundry, moduleRoleId) &&
      isEqual(sessionFoundry.contractSummary, 'UFDA 2') &&
      !isNonActiveStatus(sessionFoundry.contractStatus)
    ) {
      return this.updateSessionFoundryBasedOnPermissions(sessionFoundry.id, sessionFoundry.name, setSessionFoundry)
    }

    if (needsFallback) {
      foundryData = applyModuleFoundryFallback({
        foundries,
        moduleRoleId,
        preferUfda2: true,
        updateSession: (id, name) => this.updateSessionFoundryBasedOnPermissions(id, name, setSessionFoundry),
        router: this.$router,
        includeTerminatedAdmin: TERMINATED_ADMIN_FALLBACK_TABS.has(tabName)
      })
    }

    if (foundryData.foundryName === undefined) {
      foundryData.foundryName = window.localStorage.getItem('sessionFoundryName')
      foundryData.foundryId = window.localStorage.getItem('sessionFoundryId')
    }
    return foundryData
  },

  getUserRoles(data) {
    let roles = data.roles
    let impersonateUser = JSON.parse(window.localStorage.getItem('FS-Impersonate'))
    if (impersonateUser) {
      return 'existingUser'
    }
    for (let role of roles) {
      if (role === 'foundrySupport') {
        return 'foundrySupport'
      }
      if (data.applicationStatus === 'exists' && data.foundries.length > 0) {
        return 'existingUser'
      }
    }

    if (roles.length === 0 && data.foundries.length === 0 && data.applicationStatus !== 'exists') {
      return 'newUser'
    }
  },
  async parseCSVFile(file) {
    try {
      const content = await file.text();
      const lines = content.split(/\r?\n/).filter(line => line.trim() !== '');
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase());
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const values = [];
        let current = '';
        let insideQuotes = false;

        for (let j = 0; j < line.length; j++) {
          const char = line[j];
          if (char === '"' && line[j + 1] === '"') {
            current += '"';
            j++;
          } else if (char === '"') {
            insideQuotes = !insideQuotes;
          } else if (char === ',' && !insideQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());

        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        rows.push(row);
      }
      return {
        headers,
        rows,
        totalRows: rows.length,
        totalColumns: headers.length,
        fileName: file.name
      };
    }
    catch (error) {
      throw new Error(`Error parsing CSV: ${error.message}`);
    }
  },
  getRouteTabName() {
    let routeName = this.$route?.name
    if (routeName === 'home' || routeName === 'submissions_id') {
      return 'submissions'
    }
    if (routeName === 'promos' || routeName === 'new_promo' || routeName === 'edit_promo' || routeName === 'view_promo') {
      return 'promotions'
    }
    if (routeName === 'earnings_report_dashboard' || routeName === 'quarterly_statements') {
      return 'salesReport'
    }
    if (
      routeName === 'payment_dashboard' ||
      routeName === 'payment_accounts' ||
      routeName === 'payment_new_data_manager' ||
      routeName === 'payment_edit_data_manager' ||
      routeName === 'payment_account_error'
    ) {
      return 'payment'
    }
    if (routeName === 'contract') {
      return 'agreements'
    }
    if (routeName === 'foundry_profile' || routeName === 'edit_foundry') {
      return 'foundryProfile'
    }
    if (routeName === 'user_permissions') {
      return 'userManagement'
    }
    if (routeName === 'BundleDashboard' || routeName === 'BundleSubmissionDraft' || routeName === 'BundleSuccess') {
      return 'bundles'
    }
  },

  emitFunctionsbasedonRoute() {
    if (this.$route.name === 'home') {
      this.$events.$emit('get_submissions', true)
    }
    if (this.$route.name === 'promos') {
      this.$events.$emit('get_promotions')
    }
    if (this.$route.name === 'earnings-report') {
      this.$events.$emit('get_earningsreport')
    }
    if (this.$route.name === 'agreements') {
      this.$events.$emit('get_agreements')
    }
    if (this.$route.name === 'payment') {
      this.$events.$emit('get_payments')
    }
    if (this.$route.name === 'user_permissions') {
      this.$events.$emit('get_userpermissions')
    }
  },

  routeAfterLogin(defaultFoundry, foundries, rolesArr, applicationStatus) {
    if (rolesArr && rolesArr.includes('foundrySupport')) {
      return AppRoutesUrl.FOUNDRY_APPLICATIONS
    } else if (rolesArr?.includes('bulkIngestionUser')) {
      return AppRoutesUrl.INVENTORY_INGESTION_HUB
    } else if (rolesArr?.includes('dropShipUser')) {
      return AppRoutesUrl.DROPSHIP
    } else {
      return AppRoutesUrl.DASHBOARD
    }
  },

  getSortIcons(columnName) {
    return {
      sortAscending: `<md-icon-sort-asc data-qa-id="${columnName}_ascending_sort-icon" rotate="up" size="small" icontitle="enabled" color="spirits--700"></md-icon-sort-asc>`,
      sortDescending: `<md-icon-sort data-qa-id="${columnName}_descending_sort-icon" rotate="up" size="small" icontitle="enabled" color="spirits--700"></md-icon-sort>`,
      sortUnSort: `<md-icon-sort-inactive data-qa-id="${columnName}_unsort-icon" rotate="up" size="small" icontitle="enabled" color="spirits--600"></md-icon-sort-inactive>`
    }
  },

  setFoundryContractStatus(foundryId) {
    const foundries = JSON.parse(window.localStorage.getItem('userFoundries'))
    const contractSummary = foundries
      ?.filter(function (item) {
        return item.id === foundryId
      })
      .map(function (item) {
        return item.contractSummary
      })
    const status = contractSummary == 'UFDA 2' ? true : false
    this.$store.commit('contract/setContractStatus', status)
  },

  truncate(source, size = 25) {
    if (isEmpty(source)) return source
    return source.length > size ? source.slice(0, size - 1) + '…' : source
  },

  foundryHasModulePermission(moduleName, foundriesList = [], foundryId) {
    const currentFoundry = foundriesList.find(foundry => foundry.id === foundryId)
    const currentFoundryPermissions = currentFoundry?.permissions

    if (!isEmpty(currentFoundryPermissions)) {
      return currentFoundryPermissions.find(singlePerm => singlePerm.name === moduleName)
    }

    return false
  },

  setPopupButtonText(currentPage) {
    const challangesData = JSON.parse(localStorage.getItem('challengesData'));
    const currentPageIncompleteChallenges = challangesData?.filter(challenge => challenge.taskRoute === currentPage && challenge.taskStatus === 'inComplete');
    if (currentPageIncompleteChallenges.length > 0) {
      return {
        name: currentPageIncompleteChallenges[0].taskName,
        route: currentPageIncompleteChallenges[0].taskRoute
      };
    }
    const nextIncompleteChallenges = challangesData?.filter(challenge => challenge.taskStatus === 'inComplete');
    if (nextIncompleteChallenges.length > 0) {
      return {
        name: nextIncompleteChallenges[0].taskName,
        route: nextIncompleteChallenges[0].taskRoute
      };
    }
    return 'all_completed';
  },

  updateChallengeData(currentRoute, status) {
    const challengesData = JSON.parse(localStorage.getItem('challengesData'))
    challengesData?.forEach(challenge => {
      if (challenge.taskRoute === currentRoute) {
        challenge.taskStatus = status;
      }
    });
    localStorage.setItem('challengesData', JSON.stringify(challengesData));
  },

  // To check the permissions for a module for a foundry, you can use the following function:
  isFoundryHaveModulePermission(foundryID, permission) {
    let allFoundries = JSON.parse(localStorage.getItem('userFoundries'));
    let permissionArray = allFoundries?.find((item) => item.id == foundryID)?.permissions;
    if (permissionArray) {
      permissionArray = permissionArray.map(item => item.id);
      if (permissionArray.indexOf(getRoleIdMapping()['foundryProfile']) != -1) {
        permissionArray[permissionArray.indexOf(getRoleIdMapping()['foundryProfile'])] = 'edit_foundry'
      }
      if (permissionArray.indexOf(getRoleIdMapping()['agreements']) != -1) {
        permissionArray[permissionArray.indexOf(getRoleIdMapping()['agreements'])] = 'post_contract'
      }
      return permissionArray.includes(permission);
    }
  },

  getAgGridLatestRowData(gridApi) {
    const latestRowData = [];
    gridApi?.forEachNodeAfterFilterAndSort((node) => {
      latestRowData.push(node.data);
    });
    return latestRowData
  },
  csvEscape,
  getMaterialNumberForBinary,
  getSingleStylePackageByBinary,
  buildIngestionCSV
}

const formatDate = (utcDate, locale, format = 'LL') => {
  dayjs.locale(locale)
  const timeZone = dayjs.tz.guess()
  return dayjs(utcDate).tz(timeZone).format(format)
}

const getAmount = (amount, currencyType) => {
  return isValidValue(currencyType)
    ? Number(amount).toLocaleString('en-US', {
      style: 'currency',
      currency: currencyType,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    : amount
}


const isValidValue = value => {
  return !(
    typeof value === 'undefined' ||
    value === null ||
    value === '' ||
    (typeof value === 'number' && Number.isNaN(value))
  )
}

const getAppEnvironment = () => {
  const currentDomain = window.location.origin
  if (!isValidValue(currentDomain)) return 'production'

  switch (currentDomain) {
    case Common.PROD_DOMAIN: {
      return 'production'
    }
    case Common.PROD_EKS_DOMAIN: {
      return 'prod_eks'
    }
    case Common.PRE_PROD_DOMAIN: {
      return 'preprod'
    }
    case Common.UAT_DOMAIN: {
      return 'uat'
    }
    case Common.PRE_PROD_DOMAIN_CERBERUS: {
      return 'cer'
    }
    case Common.PRE_PROD_DOMAIN_UPGRADED: {
      return 'preprod-8.2'
    }
    case Common.PRE_PROD_DOMAIN_CERBERUS_2: {
      return 'cer2'
    }
    case Common.PRE_PROD_DOMAIN_ALEXANDRIA: {
      return 'alex'
    }
    default: {
      return 'local'
    }
  }
}

const getEnvMetadata = () => {
  const currentDomain = window.location.origin
  if (!isValidValue(currentDomain)) return 'production'

  switch (currentDomain) {
    case Common.PROD_DOMAIN: {
      return {
        env: 'production',
        customFoundryId: '1Xaz618PskKuqShCZPTqNxB3'
      }
    }
    case Common.PROD_EKS_DOMAIN: {
      return {
        env: 'prod_eks',
        customFoundryId: '1Xaz618PskKuqShCZPTqNxB3'
      }
    }
    case Common.PRE_PROD_DOMAIN: {
      return {
        env: 'preprod',
        customFoundryId: 'kOUQNHEoQicKGUQQ91e2BRPr'
      }
    }
    case Common.UAT_DOMAIN: {
      return {
        env: 'uat',
        customFoundryId: 'kOUQNHEoQicKGUQQ91e2BRPr'
      }
    }
    case Common.PRE_PROD_DOMAIN_CERBERUS: {
      return {
        env: 'cer',
        customFoundryId: 'kOUQNHEoQicKGUQQ91e2BRPr'
      }
    }
    case Common.PRE_PROD_DOMAIN_UPGRADED: {
      return {
        env: 'preprod-8.2',
        customFoundryId: 'kOUQNHEoQicKGUQQ91e2BRPr'
      }
    }
    case Common.PRE_PROD_DOMAIN_CERBERUS_2: {
      return {
        env: 'cer2',
        customFoundryId: 'kOUQNHEoQicKGUQQ91e2BRPr'
      }
    }
    case Common.PRE_PROD_DOMAIN_ALEXANDRIA: {
      return {
        env: 'alex',
        customFoundryId: 'kOUQNHEoQicKGUQQ91e2BRPr'
      }
    }
    default: {
      return {
        env: 'local',
        customFoundryId: 'wO2jOw8sVnyJIy5qNm2YFSLV'
      }
    }
  }
}

const getAuth0CredDetails = () => {
  switch (getAppEnvironment()) {
    case 'production': {
      return {
        auth0ClientId: 'OTXXdIsxljZfoU3Uh4cheKByLVCXPDCd',
        auth0DomainName: 'https://secure.monotype.com',
        auth0RedirectUri: 'https://fontplatform.monotype.com/login_page',
        auth0LogoutRedirectUri: 'https://fontplatform.monotype.com/logout',
        auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform.monotype.com/login',
        accountsUrl: 'https://accounts.monotype.com/api/initLogin',
        sigUrl: 'https://sig.monotype.com',
        mfpUrl: 'https://fontplatform.monotype.com'
      }
    }
    case 'prod_eks': {
      return {
        auth0ClientId: 'OTXXdIsxljZfoU3Uh4cheKByLVCXPDCd',
        auth0DomainName: 'https://secure.monotype.com',
        auth0RedirectUri: 'https://fontplatform-eks.monotype.com/login_page',
        auth0LogoutRedirectUri: 'https://fontplatform-eks.monotype.com/logout',
        auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform-eks.monotype.com/login',
        accountsUrl: 'https://accounts.monotype.com/api/initLogin',
        sigUrl: 'https://sig.monotype.com',
        mfpUrl: 'https://fontplatform-eks.monotype.com'
      }
    }
    case 'cer': {
      return {
        auth0ClientId: 'Fya8dLMhdcFED0CDOFhti2yZZHQjeAJT',
        auth0DomainName: 'https://secure-pp.monotype.com',
        auth0RedirectUri: 'https://fontplatform-eks-cerberus.monotype-dev.com/login_page',
        auth0LogoutRedirectUri: 'https://fontplatform-eks-cerberus.monotype-dev.com/logout',
        auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform-eks-cerberus.monotype-dev.com/login',
        accountsUrl: 'https://pp-accounts.monotype.com/api/initLogin',
        sigUrl: 'https://sig-preprod.monotype.com',
        mfpUrl: 'https://fontplatform-eks-cerberus.monotype-dev.com'
      }
    }
    case 'alex': {
      return {
        auth0ClientId: 'Fya8dLMhdcFED0CDOFhti2yZZHQjeAJT',
        auth0DomainName: 'https://secure-pp.monotype.com',
        auth0RedirectUri: 'https://fontplatform-eks-alex.monotype-dev.com/login_page',
        auth0LogoutRedirectUri: 'https://fontplatform-eks-alex.monotype-dev.com/logout',
        auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform-eks-alex.monotype-dev.com/login',
        accountsUrl: 'https://pp-accounts.monotype.com/api/initLogin',
        sigUrl: 'https://sig-preprod.monotype.com',
        mfpUrl: 'https://fontplatform-eks-alex.monotype-dev.com'
      }
    }
    case 'preprod': {
      return {
        auth0ClientId: 'Fya8dLMhdcFED0CDOFhti2yZZHQjeAJT',
        auth0DomainName: 'https://secure-pp.monotype.com',
        auth0RedirectUri: 'https://fontplatform-eks.monotype-pp.com/login_page',
        auth0LogoutRedirectUri: 'https://fontplatform-eks.monotype-pp.com/logout',
        auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform-eks.monotype-pp.com/login',
        accountsUrl: 'https://pp-accounts.monotype.com/api/initLogin',
        sigUrl: 'https://sig-preprod.monotype.com',
        mfpUrl: 'https://fontplatform-eks.monotype-pp.com'
      }
    }
    case 'uat': {
      return {
        auth0ClientId: 'AQQJuAKRWLTkUFPi19FIRCU25MYC8jmX',
        auth0DomainName: 'https://secure-pp.monotype.com',
        auth0RedirectUri: 'https://fontplatform-eks-qa.monotype-pp.com/login_page',
        auth0LogoutRedirectUri: 'https://fontplatform-eks-qa.monotype-pp.com/logout',
        auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform-eks-qa.monotype-pp.com/login',
        accountsUrl: 'https://pp-accounts.monotype.com/api/initLogin',
        sigUrl: 'https://sig-preprod.monotype.com',
        mfpUrl: 'https://fontplatform-eks-qa.monotype-pp.com'
      }
    }
    case 'cer2': {
      return {
        auth0ClientId: 'Fya8dLMhdcFED0CDOFhti2yZZHQjeAJT',
        auth0DomainName: 'https://secure-pp.monotype.com',
        auth0RedirectUri: 'https://fontplatform-eks-cerberus2.monotype-dev.com/login_page',
        auth0LogoutRedirectUri: 'https://fontplatform-eks-cerberus2.monotype-dev.com/logout',
        auth0LogoutRedirectUriForUnauthorisedUser: 'https://fontplatform-eks-cerberus2.monotype-dev.com/login',
        accountsUrl: 'https://pp-accounts.monotype.com/api/initLogin',
        sigUrl: 'https://sig-preprod.monotype.com',
        mfpUrl: 'https://fontplatform-eks-cerberus2.monotype-dev.com'
      }
    }
    default: {
      return {
        auth0ClientId: 'Ll9lH43YytzEU5ql1VU4eGep77Y3IZI9',
        auth0DomainName: 'https://secure-test.monotype.com',
        auth0RedirectUri: 'http://127.0.0.1/login_page',
        auth0LogoutRedirectUri: 'http://127.0.0.1/logout',
        auth0LogoutRedirectUriForUnauthorisedUser: 'http://127.0.0.1/login',
        accountsUrl: 'https://pp-accounts.monotype.com/api/initLogin',
        sigUrl: 'https://sig-preprod.monotype.com',
        mfpUrl: 'http://127.0.0.1'
      }
    }
  }
}

const getRoleIdMapping = () => {
  switch (getAppEnvironment()) {
    case 'production': 
    case 'prod_eks': {
      return {
        payment: 'rol_kvMzmVtaYF17fJb1',
        agreements: 'rol_fhXssqrz3Q7ojeT4',
        submissions: 'rol_sV8nXAGlHjRSpTVq',
        promotions: 'rol_eU42r45ioy7ClmhO',
        salesReport: 'rol_LmkUB88vp11YDrz7',
        foundryProfile: 'rol_ZknulK4GaMg3uCcJ',
        userManagement: 'rol_seKx5lLLmL62scJL',
        bundles: 'rol_KPK7Zu5o5usop49s'
      }
    }
    case 'preprod':
    case 'uat':
    case 'cer':
    case 'cer2':
    case 'preprod-8.2':
    case 'man':
    case 'man2':
    case 'alex':
      {
        return {
          payment: 'rol_BMWWEgKV58MLBrU6',
          agreements: 'rol_3mC26mQrm4EZ6wUv',
          submissions: 'rol_VrF66a3He3Q3R8R5',
          promotions: 'rol_tgvaxztzYh6yLdtr',
          salesReport: 'rol_OjArK43RzLVu5gVi',
          foundryProfile: 'rol_CdiiMbtOSCWXtnV7',
          userManagement: 'rol_T2nxZM7vmUwedjq9',
          bundles: 'rol_5T5E3x45s0TCakrf'
        }
      }
    default: {
      return {
        payment: 'rol_UHdtUUtpeZjSPogy',
        agreements: 'rol_uV6674P0u06i0Nnj',
        submissions: 'rol_D5U7UO2rcJudZGXX',
        promotions: 'rol_nXO4DXeOr1F8VwzL',
        salesReport: 'rol_TMF6v7JhuJjUTM1J',
        foundryProfile: 'rol_km64tpDqoei0LzpU',
        userManagement: 'rol_Kui1R6NwwePzyWRj',
        bundles: 'rol_hotgn2KLhRxZVKRy'
      }
    }
  }
}

const TERMINATED_ADMIN_FALLBACK_TABS = new Set(['payment', 'agreements', 'salesReport'])

const hasModulePermission = (foundry, moduleRoleId) =>
  foundry?.permissions?.some(p => p.id === moduleRoleId)

const shouldPickAnotherFoundry = (
  defaultFoundry,
  foundries,
  sessionFoundryId,
  moduleRoleId,
  { keepSessionWithPermission = true } = {}
) => {
  if (
    defaultFoundry?.id === sessionFoundryId &&
    hasModulePermission(defaultFoundry, moduleRoleId) &&
    !isNonActiveStatus(defaultFoundry.contractStatus)
  ) {
    return false
  }
  if (keepSessionWithPermission) {
    const sessionFoundry = foundries.find(f => f.id === sessionFoundryId)
    if (
      sessionFoundry &&
      hasModulePermission(sessionFoundry, moduleRoleId) &&
      !isNonActiveStatus(sessionFoundry.contractStatus)
    ) {
      return false
    }
  }
  return true
}

const pickFoundry = (list, preferUfda2) =>
  preferUfda2 ? list.find(f => f.contractSummary === 'UFDA 2') || list[0] : list[0]

const applyModuleFoundryFallback = ({
  foundries,
  moduleRoleId,
  preferUfda2,
  updateSession,
  router,
  includeTerminatedAdmin = false
}) => {
  const active = foundries.filter(
    f => !isNonActiveStatus(f.contractStatus) && hasModulePermission(f, moduleRoleId)
  )
  if (active.length) {
    const chosen = pickFoundry(active, preferUfda2)
    return updateSession(chosen.id, chosen.name)
  }

  if (includeTerminatedAdmin) {
    const adminTerminated = foundries.filter(
      f => isNonActiveStatus(f.contractStatus) && f.isAdmin === true && hasModulePermission(f, moduleRoleId)
    )
    if (adminTerminated.length) {
      const chosen = pickFoundry(adminTerminated, preferUfda2)
      return updateSession(chosen.id, chosen.name)
    }
  }

  const foundriesTerminated = window.localStorage.getItem('foundriesTerminated') || CONSOLIDATED.NONE
  if (
    foundriesTerminated !== CONSOLIDATED.NONE &&
    router?.currentRoute?.value?.name !== TERMINATED_CONTRACT
  ) {
    router.push({ name: TERMINATED_CONTRACT })
    return {
      foundryId: window.localStorage.getItem('sessionFoundryId'),
      foundryName: window.localStorage.getItem('sessionFoundryName'),
      redirected: true
    }
  }
  return {
    foundryId: window.localStorage.getItem('sessionFoundryId'),
    foundryName: window.localStorage.getItem('sessionFoundryName'),
    redirected: false
  }
}

const resolveModuleSessionFoundry = ({
  defaultFoundry,
  foundries,
  tabName,
  sessionFoundryId,
  getRoleIdMapping,
  updateSession,
  router
}) => {
  const moduleRoleId = getRoleIdMapping()[tabName]
  let foundryData = {}
  if (shouldPickAnotherFoundry(defaultFoundry, foundries, sessionFoundryId, moduleRoleId)) {
    foundryData = applyModuleFoundryFallback({
      foundries,
      moduleRoleId,
      updateSession,
      router,
      includeTerminatedAdmin: TERMINATED_ADMIN_FALLBACK_TABS.has(tabName)
    })
  }

  if (foundryData.foundryName === undefined) {
    foundryData.foundryName = window.localStorage.getItem('sessionFoundryName')
    foundryData.foundryId = window.localStorage.getItem('sessionFoundryId')
  }
  return foundryData
}

const getCurrentSessionFoundryId = (store) =>
  store?.getters?.['profile/sessionFoundryId'] ?? window.localStorage.getItem('sessionFoundryId') ?? ''

const dispatchSessionFoundryIfChanged = (foundryId, foundryName, store, setSessionFoundry = true) => {
  if (setSessionFoundry && foundryId && foundryId !== getCurrentSessionFoundryId(store)) {
    store.dispatch('auth/setSessionFoundry', { id: foundryId, name: foundryName })
  }
  return { foundryId, foundryName }
}

/** this function will execute on click of navigation tab after foundries call */
const setFoundryForModuleOnNavigation = (
  defaultFoundry,
  foundries,
  tabName,
  sessionFoundryId,
  store,
  router = null
) => {
  return resolveModuleSessionFoundry({
    defaultFoundry,
    foundries,
    tabName,
    sessionFoundryId,
    getRoleIdMapping,
    updateSession: (id, name) => dispatchSessionFoundryIfChanged(id, name, store),
    router
  })
}

const CDN_URL =
  getAppEnvironment() === 'production' || getAppEnvironment() === 'prod_eks' ? 'https://cdn-foundry-eks.monotype.com' : 'https://cdn-foundry-pp-eks.monotype-pp.com'


const handleFilesValidation = (file) => {
  return new Promise((resolve, reject) => {
    const maxFileSize = process.env.MAX_FILE_SIZE;
    const maxFileSizeInMB = Math.floor(maxFileSize / (1024 * 1024));
    const allowedExtensions = Common.FONTS_ALLOWED_EXTENSIONS;
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      const err = { name: file.name, message: t('pages.inventory_ingestion_hub.dropzone.unsupported_file_format') }
      return reject(err);
    }

    if (file.size > maxFileSize) {
      const err = { name: file.name, message: t('pages.inventory_ingestion_hub.dropzone.file_size_reached', { maxFileSize: maxFileSizeInMB }) };
      return reject(err);
    }

    resolve(file);
  })
}

const handleFilesProcessing = (files) => {
  return new Promise((resolve, reject) => {
    let md5Array = [];
    for (let file of files) {
      calculateMD5(file).then(md5 => {
        md5Array.push(md5);
      })
    }
    resolve(md5Array);
  })
}

const calculateMD5 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = function (event) {
      const md5 = SparkMD5.ArrayBuffer.hash(event.target.result);
      resolve(md5);
    };
    reader.onerror = function (error) {
      reject(error);
    };
    reader.readAsArrayBuffer(file);
  });
}

const createImagePreSignedURL = async (options, alreadyUploadedMd5 = [], submissionIdOrRelation = '', isSubmissionImages = false, imageType = '') => {
  let imageMD5Array = [];
  let imageMD5WithExtensionArray = [];
  let submissionImageNamesArry = [];
  let totalFile;

  for (let i = 0; i < options.length; i++) {
    let md5 = await calculateMD5(options[i].file);
    options[i].md5 = md5;
    let fileExtension = options[i].file.type.split('/')[1];
    imageMD5WithExtensionArray.push(md5 + '.' + fileExtension);
  }

  options = options.filter(opt => {
    if (alreadyUploadedMd5.some(item => {
      return (typeof item == 'object' ? item.md5 || item.md5Checksum : item) == opt.md5
    })
    ) {
      Toaster.notifyError(t('pages.inventory_ingestion_hub.image_drop_zone.image_already_exists', {
        fileName: opt.file.name
      }));
      return false;
    }
    let fileExtension = opt.file.type.split('/')[1];
    submissionImageNamesArry.push(
      {
        'md5': opt.md5 + '.' + fileExtension,
        'fileName': opt.file.name
      });
    return true;
  });
  if (options.length === 0) {
    throw new Error('No new images to upload.');
  }

  if (submissionIdOrRelation === 'bundle') {
    const typeToCheck = imageType === 1 ? 'main' : 'inspirational';
    const imageTypeCount = alreadyUploadedMd5.filter(
      (file) => file.imageType === typeToCheck
    ).length;
    totalFile = imageTypeCount + options.length;
  } else {
    totalFile = alreadyUploadedMd5.length + options.length;
  }

  if (totalFile > maxFilesAccepted) {
    Toaster.notifyError(t('pages.inventory_ingestion_hub.image_drop_zone.upload_exceeded'));
    throw new Error('Only a maximum of 15 images can be uploaded');
  }

  imageMD5Array = imageMD5WithExtensionArray.filter(md5WithExt => {
    const md5 = md5WithExt.split('.')[0];
    return !alreadyUploadedMd5.includes(md5);
  });

  if (imageMD5Array.length === 0) {
    throw new Error('No new images to upload.');
  }
  let presignedURLArray = ''
  if (isSubmissionImages) {
    presignedURLArray = await Api.Images.createPresignedUrlForImages(submissionIdOrRelation, { keys: submissionImageNamesArry })
      .catch((error) => {
        Toaster.notifyError(t('pages.submissions.components.graphics.poster.upload_failed'));
        throw error;
      })
  }
  else {
    presignedURLArray = await Api.Asset.createPresignedUrlForAsset(submissionIdOrRelation, { keys: imageMD5Array })
      .catch((error) => {
        Toaster.notifyError(t('pages.submissions.components.graphics.poster.upload_failed'));
        throw error;
      });
  }
  try {
    presignedURLArray = presignedURLArray.data.result;

    // Map the presignedURLArray to create an array of promises for parallel execution
    const uploadPromises = presignedURLArray.map((presignedURL, index) => {
      let md5Hash = presignedURL.md5;

      if (md5Hash.includes('_')) {
        const parts = md5Hash.split('_');
        md5Hash = parts[1];
      }
      const option = options[imageMD5Array.indexOf(md5Hash)];
      if (presignedURL.signedUrl) {
        return uploadOnPreSigned(option, presignedURL.signedUrl);  // Return promise
      }
      return null;  // If there's no signedUrl, return null (or handle this case differently)
    }).filter(Boolean);  // Remove null entries if necessary

    await Promise.allSettled(uploadPromises);
    return options;
  } catch (error) {
    throw error;  // Re-throw the error if needed
  }
}

const uploadOnPreSigned = async (option, presignedURL) => {
  try {
    const response = await fetch(presignedURL, {
      method: 'PUT',
      body: option?.file,
      headers: {
        'Content-Type': option?.file.type,
      },
    });
    if (response.ok) {
      // Do something after the fetch is successful
      return option;
    } else {
      console.error('Failed to upload file:', option?.file.name);
    }
  } catch (error) {
    throw error;
  }
}

const handleImagesErrorTimeline = () => {
  const totalImages = imagesList.value.length;
  const isError = isCustomProject.value ? (totalImages === 0 || totalImages < 5) : totalImages < 5;
  store.commit('inventoryingestion/setFamilyTimelinesErrorStatusObj', {
    images: isError
  });
}

const formatDateSis = (isoString) => {
  const date = new Date(isoString);
  return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    }).format(date);
}

const convertToISO = (dateString) => {
  const date = dayjs.utc(dateString, 'MMM DD, YYYY');
  return {
    isoDate: date.toISOString(), // Convert to ISO format
    year: date.year()            // Extract the year
  };
}

const sisValidationErrorsForCorruptedFiles = (binary) => {
  const validationError = binary?.validationResult?.validations?.find(validation => validation.errorLevel === 'ERROR')?.errorMessage

  let errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.misc_error');

  if (binary?.avStatus === 'false') {
    errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.malware_error');
  } else {
    switch (validationError) {
      case 'edit_scenario':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.edit_scenario');
        break;
      case 'metadata_failed':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.metadata_failed');
        break;
      case 'validation_failed':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.validation_failed');
        break;
      case 'sanitation_failed':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.sanitation_failed');
        break;
      case 'processor_error':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.processor_error');
        break;
      case 'lock_for_edit':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.lock_for_edit');
        break;
      case 'part_of_other_inventory':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.part_of_other_inventory');
        break;
      case 'release_date_error':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.release_date_error');
        break;
      case 'file_already_exists':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.file_already_exists');
        break;
      case 'pim_sync_error':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.pim_sync_error');
        break;
      case 'part_of_submission':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.part_of_submission');
        break;
      case 'family_skus_not_available':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.family_skus_not_available');
        break;
      case 'combo_sku_issue':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.combo_sku_issue');
        break;
      case 'non_custom_family_error':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.non_custom_family_error');
        break;
      case 'custom_family_error':
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.custom_family_error');
        break;
      default:
        errorMessage = t('pages.inventory_ingestion_hub.error_files_modal.corrupted_files_messages.misc_error');
    }
  }

  return errorMessage;
}

/**
 * Toggles page scroll lock when a modal is opened or closed.
 *
 * @param {boolean} isModalOpen - Whether the modal is currently open.
 * @param {boolean} useDocument - If true, scroll locking is applied to the <html> element (document.documentElement);
 *                                 if false, it's applied to the <body>. Defaults to false for backward compatibility.
 *
 * Why use documentElement (html) over body?
 * -----------------------------------------
 * - Setting `overflow: hidden` on <html> is safer and more consistent across browsers,
 *   especially when the page has complex layouts or deeply nested scrolling content.
 * - It avoids layout issues like collapsed body height, white gaps, or scroll jumps that
 *   often happen when applying `position: fixed` or `overflow` styles on <body>.
 * - It ensures background scroll is completely disabled without affecting modal layout or scroll.
 */
const disableScrollOnModalOpen = (isModalOpen, useDocument = false) => {
  const target = useDocument ? document.documentElement : document.body;
  const overflowProp = useDocument ? 'overflow' : 'overflowY';
  target.style[overflowProp] = isModalOpen ? 'hidden' : 'auto';
};

/**
 * The function `addDefaultPricesInSubmissionData` adds default prices to binaries in the user metadata
 * if no prices are provided.
 * @param data - The `data` parameter in the `addDefaultPricesInSubmissionData` function is an object
 * that contains a property `userMetaData`, which is also an object. Within `userMetaData`, there is an
 * array `binaries` that contains objects representing binary data. Each binary object may have a `
 */
const addDefaultPricesInSubmissionData = (data) => {
  data?.userMetaData?.binaries?.forEach(binary => {
    if (!binary.prices || binary.prices.length === 0) {
      binary.prices = [{ currency: Common.DEFAULT_CURRENCY, price: 0 }];
    }
  });
}

const blinkCursor = (element) => {
  element.style.cursor = 'copy';
  setTimeout(() => {
    element.style.cursor = '';
  }, 300);
}

const copyToClipboard = (element, value) => {
  if (value) {
    navigator.clipboard.writeText(value)
      .then(() => {
        blinkCursor(element);
      })
      .catch(err => console.error('Copy failed:', err));
  }
}

/**
 * The function `isAgGridCellTextTruncated` checks if the text content of a cell in an Ag-Grid table is
 * truncated.
 * @param params - The `params` object likely contains information about the cell in an Ag-Grid table,
 * such as the column, node, and possibly other relevant data. The function `isAgGridCellTextTruncated`
 * uses this information to determine if the text content of a cell is truncated based on its scroll
 * @returns The function `isAgGridCellTextTruncated` returns a boolean value indicating whether the
 * text content of a cell in an Ag-Grid table is truncated or not. If the text content is truncated,
 * the function returns `true`, otherwise it returns `false`.
 */
const isAgGridCellTextTruncated = (params) => {
  const colId = params?.column?.colId, rowIndex = params?.node?.rowIndex;
  let truncated = false;
  const cellElement = document.querySelector(
    `.ag-row[row-index="${rowIndex}"] .ag-cell[col-id="${colId}"]`
  );
  let textElement = cellElement ? cellElement.querySelector('.ag-cell-value') : null;
  textElement = textElement || cellElement
  if (textElement) {
    truncated = textElement.scrollWidth > textElement.offsetWidth;
  } else {
    // do nothing
  }
  return truncated;
}

const getValueByPath = (obj, path) => {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
};

/**
 * The function `sortSubsetByOrder` sorts an array based on the order of keys in another array.
 * @param originalArray - The `originalArray` parameter in the `sortSubsetByOrder` function refers to
 * the array that contains the original order or sequence based on which you want to sort another
 * array.
 * @param arrayToSort - An array that you want to sort based on the order of another array.
 * @param keyOfOriginalArray - The `keyOfOriginalArray` parameter is the key or path to access the
 * value in each object of the `originalArray` that will be used for sorting.
 * @param keyOfArrayToSort - The `keyOfArrayToSort` parameter is the key or path in each object of the
 * `arrayToSort` that you want to use for sorting the subset. It is used to extract the value that will
 * be used for comparison during the sorting process.
 * @returns The `sortSubsetByOrder` function returns a sorted copy of the `arrayToSort` array based on
 * the order of keys extracted from the `originalArray` and `arrayToSort` using the provided
 * `keyOfOriginalArray` and `keyOfArrayToSort` respectively.
 */
const sortSubsetByOrder = (originalArray, arrayToSort, keyOfOriginalArray, keyOfArrayToSort) => {
  const orderMap = new Map();

  originalArray.forEach((item, index) => {
    const key = getValueByPath(item, keyOfOriginalArray);
    orderMap.set(key, index);
  });

  return arrayToSort.slice().sort((a, b) => {
    const keyA = getValueByPath(a, keyOfArrayToSort);
    const keyB = getValueByPath(b, keyOfArrayToSort);
    const indexA = orderMap.get(keyA) ?? Infinity;
    const indexB = orderMap.get(keyB) ?? Infinity;
    return indexA - indexB;
  });
};


/**
 * The function `checkDuplicateElementInArray` checks for duplicate elements in an array or
 * comma-separated string input.
 * @param input - The `checkDuplicateElementInArray` function checks for duplicate elements in an array
 * or a comma-separated string. It first converts the input into an array of items, removes any leading
 * or trailing spaces, and filters out empty items. Then, it uses a `Set` to keep track of seen items
 * @returns The function `checkDuplicateElementInArray` returns `true` if there are any duplicate
 * elements in the input array or string (comma-separated values), otherwise it returns `false`.
 */
const checkDuplicateElementInArray = (input) => {
  if (!input) return false;

  const items = (Array.isArray(input) ? input : input.split(','))
    .map(item => typeof item === 'string' ? item.trim() : item)
    .filter(item => item !== '');

  const seen = new Set();
  for (const item of items) {
    if (seen.has(item)) return true;
    seen.add(item);
  }

  return false;
};

/**
 * The function `validateAlphanumeric` checks if a given value contains only alphanumeric characters,
 * commas, hyphens, and spaces.
 * @returns The function `validateAlphanumeric` returns a boolean value indicating whether the input
 * `value` consists of only alphanumeric characters (letters and numbers), commas, hyphens, and spaces.
 */
const validateAlphanumeric = value => {
  const regex = /^[a-zA-Z0-9,\- ]*$/;
  return regex.test(value)
}

/**
 * The function `isEmptyArray` checks if a given input is an empty array.
 * @param array - The `array` parameter in the `isEmptyArray` function refers to the array that is
 * being checked for emptiness. The function checks if the input is an array and if it has a length of
 * 0, indicating that it is empty.
 * @returns The function `isEmptyArray` returns `true` if the input `array` is an array and has a
 * length of 0, indicating that it is an empty array. Otherwise, it returns `false`.
 */
const isEmptyArray = (array) => {
  return Array.isArray(array) && array.length === 0;
}

const getInvalidInputStyle = () => {
  return {
    border: '1px solid var(--md-color--red-babe--500)',
    background: 'var(--md-color--red-babe--25)',
    borderRadius: 'var(--md-border-radius--md)'
  }
}

const generateCdnUrl = (url, options) => {
  // we need to modify the functions for s3 urls
  const baseUrl = new URL(url).origin; // Extract the base URL from the input URL
  const path = url.replace(baseUrl, ''); // Extract the path from the input URL
  const queryParams = Object.entries(options)
    .map(([key, value]) => `${key}=${value}`)
    .join(',');
  const cdnResizeUrlMid = Common.CDN_RESIZE_URL
  return `${baseUrl}/${cdnResizeUrlMid}/${queryParams}${path}`;
};

const isScrollPresentOnBody = (element, scrollHeight = null) => {
  const hasHorizontalScrollbar = element.scrollWidth > element.clientWidth;
  const hasVerticalScrollbar = scrollHeight || element.scrollHeight > element.clientHeight;
  return {
    hasHorizontalScrollbar,
    hasVerticalScrollbar
  }
}

const scrollToSection = (className) => {
  const element = document.querySelector(className);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  } else {
    let attempts = 0;
    const interval = setInterval(() => {
      const retryElement = document.querySelector(className);
      if (retryElement) {
        retryElement.scrollIntoView({ behavior: 'smooth' });
        clearInterval(interval);
      } else if (attempts >= 5) { // 5 attempts (2s interval * 5 = 10s)
        clearInterval(interval);
      }
      attempts++;
    }, 2000);
  }
}

// Common function to get flipfont volume bucket
const getFlipfontVolumeBucket = (count) => {
  if (!count || count <= 0) return '0'; // or any default label you want
  if (count <= 10) return '1–10';
  if (count <= 20) return '11–20';
  if (count <= 50) return '21–50';
  return '50+';
};

/**
 * Escapes CSV values to handle commas, quotes, and newlines
 * @param {*} value - Value to escape
 * @returns {String} - Escaped CSV value
 */

/**
 * Downloads data as CSV or Excel file
 * @param {Array} data - Array of objects to download
 * @param {String} format - 'csv' or 'xls' format
 * @param {String} filename - Name of the file without extension
 * @param {Array} headers - Optional custom headers array
 */
const downloadCSVorExcel = (data, format = 'csv', filename = 'download', headers = null) => {
  const DOWNLOAD_FORMAT = {
    csv: 'csv',
    xls: 'xls',
    mimeType_csv: 'text/csv;charset=utf-8',
    mimeType_xls: 'application/vnd.ms-excel'
  };

  const delimiter = format === DOWNLOAD_FORMAT.csv ? ',' : '\t';
  const mimeType = format === DOWNLOAD_FORMAT.csv ? DOWNLOAD_FORMAT.mimeType_csv : DOWNLOAD_FORMAT.mimeType_xls;
  const extension = format === DOWNLOAD_FORMAT.csv ? DOWNLOAD_FORMAT.csv : DOWNLOAD_FORMAT.xls;

  const headerRow = headers || (data.length > 0 ? Object.keys(data[0]) : []);

  // Add UTF-8 BOM for compatibility with Numbers
  let content = '\uFEFF';

  // Escape CSV fields that contain delimiters, quotes, or line breaks
  const escapeValue = value => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    const needsEscape = /[",\r\n]/.test(str);
    return needsEscape ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const header = headerRow.map(escapeValue).join(delimiter) + '\r\n';
  const rows = data
    .map(row => headerRow.map(key => escapeValue(row[key])).join(delimiter))
    .join('\r\n');

  content += header + rows;

  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const timestamp = new Date().toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).replace(/[/, ]/g, '_').replace(/:/g, '.');

  link.setAttribute('download', `${filename}_${timestamp}.${extension}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const triggerAdobeCjaEvent = (event, linkCategory, linkSection, linkName, linkType) => {
  const analyticsPayload = {
    event: event,
    links: {
      linkCategory: linkCategory,
      linkSection: linkSection,
      linkName: linkName,
      linkType: linkType
    }
  };
  adobeCjaEventEmitter(analyticsPayload);
}

/**
 * The function `splitByNewlines` takes an input string, splits it by newlines, trims each resulting
 * string, and filters out any empty strings.
 * @param input - It seems like you are trying to split a string by newlines, trim each resulting
 * string, and then filter out any empty strings. If you provide me with the input string, I can help
 * you test the `splitByNewlines` function. Could you please provide the input string you would like
 * @returns The `splitByNewlines` function is returning an array of strings that have been split by
 * newlines, trimmed of any extra whitespace, and filtered to remove any empty strings.
 */
const splitByNewlines = (input) => {
  return input
    .split(Common.REGEX_COLLECTIONS.NEW_LINE_REGEX)
    .map(str => str.trim())
    .filter(str => str.length > 0);
}

/**
 * The function retrieves the AI consent status for a specific foundry from local storage.
 * @returns The function `getSessionFoundryAiConsentFromLS` returns a boolean value indicating whether
 * the session foundry has an active AI agreement or not.
 */
const getSessionFoundryAiConsentFromLS = () => {
  let foundriesList = window.localStorage.getItem('userFoundries');
  foundriesList = JSON.parse(foundriesList);
  const sessionFoundryId = window.localStorage.getItem('sessionFoundryId');
  const sessionFoundryObj = foundriesList.find(element => element.id === sessionFoundryId);
  return Boolean(sessionFoundryObj?.aiAgreement?.currentAgreementId);
};

/**
 * The function `getRouteParam` extracts a specific parameter value from the URL hash in a JavaScript
 * environment.
 * @param key - It looks like you are providing a function `getRouteParam` that retrieves a parameter
 * value from the URL hash based on a given key. If you have any specific questions or need assistance
 * with this function, feel free to ask!
 * @returns The `getRouteParam` function is returning the value of the route parameter corresponding to
 * the provided key in the URL hash. If the key is found in the URL hash segments and there is a value
 * following it, that value is returned. Otherwise, it returns `null`.
 */
const getRouteParam = (key) => {
  const segments = window.location.pathname.split('/');
  const index = segments.indexOf(key);
  if (index !== -1 && segments.length > index + 1) {
    return segments[index + 1];
  }

  return null;
}

/**
 * Tokenizes text into words (lowercase, word boundaries only)
 * @param {string} text - Input text to tokenize
 * @returns {Array} Array of tokens
 */
const tokenize = (text) => {
  if (!text || typeof text !== 'string') return [];
  return text.toLowerCase().match(/\b\w+\b/g) || [];
}

/**
 * Creates term frequency map from tokens
 * @param {Array} tokens - Array of tokens
 * @returns {Object} Term frequency map
 */
const termFreqMap = (tokens) => {
  const map = {};
  tokens.forEach(token => {
    map[token] = (map[token] || 0) + 1;
  });
  return map;
}

/**
 * Calculates cosine similarity between two texts
 * @param {string} textA - First text
 * @param {string} textB - Second text
 * @returns {number} Cosine similarity score (0-1)
 */
const cosineSimilarity = (textA, textB) => {
  const tokensA = tokenize(textA);
  const tokensB = tokenize(textB);
  const tfA = termFreqMap(tokensA);
  const tfB = termFreqMap(tokensB);
  const allWords = new Set([...Object.keys(tfA), ...Object.keys(tfB)]);
  
  let dot = 0, magA = 0, magB = 0;
  allWords.forEach(word => {
    const a = tfA[word] || 0;
    const b = tfB[word] || 0;
    dot += a * b;
    magA += a * a;
    magB += b * b;
  });
  
  return dot / (Math.sqrt(magA) * Math.sqrt(magB) || 1);
}

/**
 * Calculates Jaccard similarity between two texts
 * @param {string} textA - First text
 * @param {string} textB - Second text
 * @returns {number} Jaccard similarity score (0-1)
 */
const jaccardSimilarity = (textA, textB) => {
  const tokensA = new Set(tokenize(textA));
  const tokensB = new Set(tokenize(textB));
  const intersection = new Set([...tokensA].filter(x => tokensB.has(x)));
  const union = new Set([...tokensA, ...tokensB]);
  
  return intersection.size / (union.size || 1);
}

/**
 * Calculates combined similarity score using both cosine and Jaccard methods
 * @param {string} textA - First text
 * @param {string} textB - Second text
 * @returns {Object} Object containing cosine, jaccard, and combined scores
 */
const combinedSimilarity = (textA, textB) => {
  if (!textA || !textB || typeof textA !== 'string' || typeof textB !== 'string') {
    return {
      cosine: 0,
      jaccard: 0,
      combined: 0
    };
  }

  const cosine = cosineSimilarity(textA, textB);
  const jaccard = jaccardSimilarity(textA, textB);
  
  return {
    cosine: cosine,
    jaccard: jaccard,
    combined: (cosine + jaccard) / 2
  };
}

/**
 * Checks if two texts exceed similarity threshold
 * @param {string} textA - First text
 * @param {string} textB - Second text
 * @param {number} threshold - Similarity threshold (0-1)
 * @returns {Object} Result object with similarity data and threshold check
 */
const checkSimilarityThreshold = (textA, textB, threshold = 0.7) => {
  const similarity = combinedSimilarity(textA, textB);
  const exceedsThreshold = similarity.combined > threshold;
  
  return {
    ...similarity,
    threshold,
    exceedsThreshold,
    percentages: {
      cosine: Math.round(similarity.cosine * 100),
      jaccard: Math.round(similarity.jaccard * 100),
      combined: Math.round(similarity.combined * 100)
    }
  };
}

/**
 * Normalizes multiple consecutive spaces to single spaces in HTML content
 * while preserving other whitespace characters like line breaks and tabs.
 */
const normalizeSpaces = (htmlContent) => {
  // Early return if htmlContent is not a string
  if (typeof htmlContent !== 'string') {
    throw new Error('normalizeSpaces expects a string input');
  }

  // Early return if htmlContent is empty
  if (!htmlContent.trim()) {
    return htmlContent;
  }

  return htmlContent.replace(Common.REGEX_COLLECTIONS.EXTRA_SPACES_PATTERN, ' ');
};

export {
  FOUNDRY_NAME_INVALID_PATTERN,
  SSN_INVALID_PATTERN,
  CDN_URL,
  removeWords,
  handleImagesErrorTimeline,
  getAppEnvironment,
  getEnvMetadata,
  getAuth0CredDetails,
  setFoundryForModuleOnNavigation,
  getAmount,
  isValidValue,
  formatDate,
  calculateMD5,
  handleFilesValidation,
  handleFilesProcessing,
  createImagePreSignedURL,
  formatDateSis,
  convertToISO,
  sisValidationErrorsForCorruptedFiles,
  disableScrollOnModalOpen,
  addDefaultPricesInSubmissionData,
  blinkCursor,
  copyToClipboard,
  isAgGridCellTextTruncated,
  getValueByPath,
  sortSubsetByOrder,
  generateCdnUrl,
  isScrollPresentOnBody,
  checkDuplicateElementInArray,
  validateAlphanumeric,
  isEmptyArray,
  getInvalidInputStyle,
  triggerAdobeCjaEvent,
  scrollToSection,
  downloadCSVorExcel,
  getRouteParam,
  splitByNewlines,
  getSessionFoundryAiConsentFromLS,
  getFlipfontVolumeBucket,
  tokenize,
  termFreqMap,
  cosineSimilarity,
  jaccardSimilarity,
  combinedSimilarity,
  checkSimilarityThreshold,
  csvEscape,
  getMaterialNumberForBinary,
  getSingleStylePackageByBinary,
  buildIngestionCSV,
  normalizeSpaces,
  isEqualIgnoreCase
}
